import React, { useRef, useState, useEffect, startTransition } from 'react';
import { flushSync } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { DOMSerializer } from 'prosemirror-model';
// @ts-ignore - BubbleMenu is exported from @tiptap/react/menus but TypeScript module resolution doesn't support it
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { Heading } from '@tiptap/extension-heading';
import { mergeAttributes } from '@tiptap/core';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Undo, Redo, Search, Pickaxe, Link as LinkIcon, Link2Off } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Figure from './FigureExtension';
import { ExtraContent } from './ExtraContentExtension';
import { FindReplaceHighlight } from './FindReplaceHighlightExtension';
import { SearchHighlight, setSearchQuery, clearSearchQuery } from './SearchHighlightExtension';
import Annotation from './AnnotationExtension';
import AnnotationDialog from './AnnotationDialog';
import AnnotationTooltip from './AnnotationTooltip';
import InfoDialog from './InfoDialog';
import './TiptapEditor.css';

import { HeadingItem } from './DocumentOutline';

// Custom Heading extension that preserves data-page attribute
// We extend the Heading extension and add our custom attribute
const CustomHeading = Heading.extend({
    name: 'heading', // Explicitly set name to ensure commands work
    addAttributes() {
        // When extending, we need to return both parent attributes and our custom ones
        // The parent Heading extension has a 'level' attribute that we must preserve
        return {
            // Parent's level attribute (required for heading functionality)
            level: {
                default: 1,
                rendered: false,
            },
            // Our custom data-page attribute
            'data-page': {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('data-page'),
                renderHTML: (attributes: Record<string, any>) => {
                    if (!attributes['data-page']) {
                        return {};
                    }
                    return { 'data-page': attributes['data-page'] };
                },
            },
        };
    },
    // Override renderHTML to include data-page in the output
    renderHTML({ node, HTMLAttributes }: { node: any; HTMLAttributes: Record<string, any> }) {
        const hasLevel = this.options.levels.includes(node.attrs.level);
        const level = hasLevel ? node.attrs.level : this.options.levels[0];
        
        // Build attrs from HTMLAttributes and add data-page if it exists
        // Note: Don't include this.options.HTMLAttributes here - it will be merged by mergeAttributes
        const attrs: Record<string, any> = { ...HTMLAttributes };
        if (node.attrs['data-page']) {
            attrs['data-page'] = node.attrs['data-page'];
        }
        
        // Merge this.options.HTMLAttributes with attrs (which contains HTMLAttributes + data-page)
        return [`h${level}`, mergeAttributes(this.options.HTMLAttributes, attrs), 0];
    },
});

interface TiptapEditorProps {
    value: string;
    onChange: (content: string) => void;
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
    height?: string;
    onEditorCreated?: (editor: any) => void;
    onFindReplace?: () => void; // Callback to open find/replace dialog
    editable?: boolean; // Whether the editor is editable (default: true)
    searchQuery?: string; // Search query to highlight in the editor
    headings?: HeadingItem[]; // Headings array for scroll-based section detection
    onVisibleSectionChange?: (sectionId: string | null) => void; // Callback when visible section changes
    onExtractPageNumbers?: (pageNumbers: Array<{ headingText: string; pageNumber: string; level: number; index: number }>) => void; // Callback to handle extracted page numbers
}

const TiptapEditor: React.FC<TiptapEditorProps> = ({
    value,
    onChange,
    placeholder = 'Write document content here...',
    className = '',
    style = {},
    height = '400px',
    onEditorCreated,
    onFindReplace,
    editable = true,
    searchQuery,
    headings = [],
    onVisibleSectionChange,
    onExtractPageNumbers
}) => {
    const { authState } = useAuth();
    const userRole = authState.user?.role;
    const isAdminOrSuperadmin = userRole === 'admin' || userRole === 'superadmin';
    
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pasteImageInputRef = useRef<HTMLInputElement>(null); // For selecting images after paste
    const directoryInputRef = useRef<HTMLInputElement>(null); // For selecting image folder
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [currentHeading, setCurrentHeading] = useState('Normal Text');
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const editorInitializedRef = useRef(false);
    const isSettingContentRef = useRef(false);
    const editorRef = useRef<any>(null);
    const originalContentRef = useRef<string | null>(null); // Store original content to prevent undo beyond it
    const scrollTimeoutRef = useRef<number | null>(null);
    const lastVisibleSectionIdRef = useRef<string | null>(null);
    const isMarkingHeadingsRef = useRef(false);
    const pendingImageReplacementsRef = useRef<string[]>([]); // Store base64 images from clipboard for paste
    const brokenImageRefsRef = useRef<Array<{ placeholder: string; filename: string; originalPath: string; index: number }>>([]); // Store broken image references for replacement
    const imageFolderMapRef = useRef<Map<string, File>>(new Map()); // Map of image paths to File objects from selected folder
    const processedImageCountRef = useRef(0); // Track processed images for progress
    const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
    const [hasTextSelection, setHasTextSelection] = useState(false);
    const [annotationElaboratedForEdit, setAnnotationElaboratedForEdit] = useState<string>('');
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [loadingImageProgress, setLoadingImageProgress] = useState({ current: 0, total: 0 });
    const [infoDialog, setInfoDialog] = useState({ isOpen: false, title: '', message: '' });
    const [isExtractingPageNumbers, setIsExtractingPageNumbers] = useState(false);

    // Debug: Log when loadingImageProgress changes
    useEffect(() => {
        if (isLoadingImages && loadingImageProgress.total > 0) {
            const percentage = Math.round((loadingImageProgress.current / loadingImageProgress.total) * 100);
            console.log('[TiptapEditor] Progress state changed:', loadingImageProgress, `(${percentage}%)`);
        }
    }, [loadingImageProgress, isLoadingImages]);

    // Helper to check if an image source is valid
    const isValidImageSource = (src: string): boolean => {
        if (!src || src.trim() === '') return false;
        
        const trimmedSrc = src.trim();
        
        // Base64 images are valid
        if (trimmedSrc.startsWith('data:image/')) return true;
        
        // HTTP/HTTPS URLs are potentially valid (we can't verify they exist, but they're valid formats)
        if (trimmedSrc.startsWith('http://') || trimmedSrc.startsWith('https://')) return true;
        
        // File protocol paths are likely broken when pasted (local file system paths)
        if (trimmedSrc.startsWith('file://') || trimmedSrc.startsWith('file:/')) return false;
        
        // Relative paths starting with ./ or ../ are likely broken when pasted
        if (trimmedSrc.startsWith('./') || trimmedSrc.startsWith('../')) return false;
        
        // Absolute paths on Windows (C:\) or Unix (/path) are likely broken when pasted
        // These are local file system paths that won't exist in the web context
        if (/^[A-Z]:\\/.test(trimmedSrc) || /^\/[^/]/.test(trimmedSrc)) return false;
        
        // If it contains common image extensions but is not a URL or data URI, it's likely a broken path
        // This catches cases like "image.png" or "path/to/image.jpg" which are local file references
        const hasImageExtension = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$|#)/i.test(trimmedSrc);
        if (hasImageExtension && !trimmedSrc.startsWith('http') && !trimmedSrc.startsWith('data:') && !trimmedSrc.startsWith('/')) {
            // Has image extension but not a URL, data URI, or absolute web path - likely broken
            return false;
        }
        
        // Allow absolute web paths starting with / (like /images/photo.png)
        // These might work if they're relative to the web server root
        
        return true;
    };

    // Transform pasted HTML to force all text to black and handle broken images
    // imageReplacements: Map of old src -> new base64 src for automatic image upload
    const transformPastedHTML = (html: string, imageReplacements?: Map<string, string>): string => {
        if (!html) return html;
        
        console.log('[TiptapEditor] transformPastedHTML called with:', {
            htmlLength: html.length,
            hasReplacements: !!imageReplacements,
            replacementCount: imageReplacements?.size || 0,
            folderMapSize: imageFolderMapRef.current.size
        });

        // Track which replacement images we've used (by index)
        let replacementIndex = 0;
        const replacementArray = imageReplacements ? Array.from(imageReplacements.values()) : [];
        
        // Clear previous broken image refs
        brokenImageRefsRef.current = [];
        let brokenImageIndex = 0;

        // Remove or fix broken image references
        // Images with invalid src (like local file paths) will cause errors
        html = html.replace(/<img([^>]*?)>/gi, (match, attributes) => {
            // Extract src attribute
            const srcMatch = attributes.match(/src=(["'])([^"']+)\1/i);
            if (srcMatch && srcMatch[2]) {
                const src = srcMatch[2];
                if (!isValidImageSource(src)) {
                    // Check if we have a replacement image from clipboard
                    if (replacementArray.length > 0 && replacementIndex < replacementArray.length) {
                        const newSrc = replacementArray[replacementIndex];
                        replacementIndex++;
                        // Replace the broken src with the base64 image
                        const quote = srcMatch[1];
                        return match.replace(srcMatch[0], `src=${quote}${newSrc}${quote}`);
                    }
                    // Try to find image in selected folder first
                    const normalizedPath = src.replace(/\\/g, '/'); // Normalize path separators
                    console.log('[TiptapEditor] transformPastedHTML - Looking for image:', normalizedPath);
                    console.log('[TiptapEditor] transformPastedHTML - Folder map size:', imageFolderMapRef.current.size);
                    console.log('[TiptapEditor] transformPastedHTML - Folder map keys (first 5):', Array.from(imageFolderMapRef.current.keys()).slice(0, 5));
                    
                    let imageFile = imageFolderMapRef.current.get(normalizedPath) || 
                                    imageFolderMapRef.current.get(src);
                    
                    // If not found by exact path, try to match by filename
                    if (!imageFile) {
                        const srcFileName = normalizedPath.split('/').pop()?.toLowerCase() || '';
                        console.log('[TiptapEditor] transformPastedHTML - Trying filename match:', srcFileName);
                        imageFile = Array.from(imageFolderMapRef.current.values()).find(file => {
                            const fileName = file.name.toLowerCase();
                            const matches = fileName === srcFileName || 
                                           fileName.includes(srcFileName) || 
                                           srcFileName.includes(fileName);
                            if (matches) {
                                console.log('[TiptapEditor] transformPastedHTML - Found match:', fileName);
                            }
                            return matches;
                        }) || undefined;
                    }
                    
                    if (imageFile) {
                        console.log('[TiptapEditor] transformPastedHTML - Found file in folder:', imageFile.name);
                        // Found matching file - convert to base64 synchronously using a data URL
                        // Since we can't do this synchronously, we'll use a unique placeholder
                        // and replace it after the image loads
                        const filename = src.split('/').pop() || src.split('\\').pop() || 'Image';
                    const altMatch = attributes.match(/alt=(["'])([^"']*)\1/i);
                        const altText = altMatch ? altMatch[2] : filename;
                        const uniqueId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                        const placeholder = `<span data-loading-image-id="${uniqueId}" style="color: #999; font-style: italic;">[Image: ${altText || 'loading...'}]</span>`;
                        
                        // Store mapping for async replacement
                        const imageMapping: { id: string; file: File; alt: string } = {
                            id: uniqueId,
                            file: imageFile,
                            alt: altText
                        };
                        
                        // Load image asynchronously and replace placeholder
                        const reader = new FileReader();
                        reader.onload = (e) => {
                            const base64 = e.target?.result as string;
                            if (base64 && editor && !editor.isDestroyed) {
                                // Replace placeholder with actual image
                                queueMicrotask(() => {
                                    const htmlContent = editor.getHTML();
                                    const placeholderRegex = new RegExp(`<span data-loading-image-id="${imageMapping.id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>\\[Image:[^\\]]+\\]</span>`, 'gi');
                                    if (placeholderRegex.test(htmlContent)) {
                                        const newHtml = htmlContent.replace(placeholderRegex, `<img src="${base64}" alt="${imageMapping.alt}" />`);
                                        if (newHtml !== htmlContent) {
                                            isSettingContentRef.current = true;
                                            editor.commands.setContent(newHtml);
                                            isSettingContentRef.current = false;
                                        }
                                    }
                                });
                            }
                        };
                        reader.readAsDataURL(imageFile);
                        
                        return placeholder;
                    }
                    
                    // No replacement available - replace with alt text or placeholder
                    // Extract filename from src for reference
                    const filename = src.split('/').pop() || src.split('\\').pop() || 'Image';
                    const altMatch = attributes.match(/alt=(["'])([^"']*)\1/i);
                    const altText = altMatch ? altMatch[2] : filename;
                    const placeholder = `<span style="color: #999; font-style: italic;">[Image: ${altText || 'broken reference'}]</span>`;
                    
                    // Store broken image reference for later replacement
                    console.log('[TiptapEditor] No match found for broken image:', src, 'Storing for later');
                    brokenImageRefsRef.current.push({
                        placeholder,
                        filename: filename,
                        originalPath: src,
                        index: brokenImageIndex++
                    });
                    
                    return placeholder;
                }
            } else if (!srcMatch) {
                // Image without src attribute - try to use replacement if available
                if (replacementArray.length > 0 && replacementIndex < replacementArray.length) {
                    const newSrc = replacementArray[replacementIndex];
                    replacementIndex++;
                    // Add src attribute
                    return match.replace(/>/, ` src="${newSrc}">`);
                }
                // No replacement - remove it
                return '';
            }
            return match; // Valid image, keep it
        });

        // Also handle images in figure tags
        html = html.replace(/<figure([^>]*?)>([\s\S]*?)<\/figure>/gi, (match, figureAttrs, figureContent) => {
            // Check if figure contains an img with broken src
            const imgMatch = figureContent.match(/<img([^>]*?)>/i);
            if (imgMatch) {
                const imgAttrs = imgMatch[1];
                const srcMatch = imgAttrs.match(/src=(["'])([^"']+)\1/i);
                if (srcMatch && srcMatch[2]) {
                    const src = srcMatch[2];
                    if (!isValidImageSource(src)) {
                        // Check if we have a replacement image from clipboard
                        if (replacementArray.length > 0 && replacementIndex < replacementArray.length) {
                            const newSrc = replacementArray[replacementIndex];
                            replacementIndex++;
                            // Replace the broken src with the base64 image
                            const quote = srcMatch[1];
                            const newImgTag = imgMatch[0].replace(srcMatch[0], `src=${quote}${newSrc}${quote}`);
                            return match.replace(imgMatch[0], newImgTag);
                        }
                        // No replacement available - replace broken figure with placeholder
                        const altMatch = imgAttrs.match(/alt=(["'])([^"']*)\1/i);
                        const altText = altMatch ? altMatch[2] : 'Image';
                        return `<p style="color: #999; font-style: italic;">[Image: ${altText || 'broken reference'}]</p>`;
                    }
                } else {
                    // Image without src - try to use replacement if available
                    if (replacementArray.length > 0 && replacementIndex < replacementArray.length) {
                        const newSrc = replacementArray[replacementIndex];
                        replacementIndex++;
                        // Add src attribute to img tag
                        const newImgTag = imgMatch[0].replace(/>/, ` src="${newSrc}">`);
                        return match.replace(imgMatch[0], newImgTag);
                    }
                }
            }
            return match; // Valid figure, keep it
        });

        // Force all text colors to black regardless of original color
        // Replace ALL color properties in inline style attributes (handles both single and double quotes)
        // Match style="..." or style='...'
        html = html.replace(/style=(["'])([^"']*)\1/gi, (match, quote, styleContent) => {
            // Replace ALL color properties with black
            let newStyle = styleContent.replace(/color:\s*[^;]+/gi, 'color: #000000');
            // If there was no color property, add it
            if (!/color\s*:/i.test(newStyle)) {
                if (newStyle) {
                    // Trim trailing semicolons and whitespace before appending
                    newStyle = newStyle.trim().replace(/;+\s*$/, '');
                    newStyle = `${newStyle}; color: #000000`;
                } else {
                    newStyle = 'color: #000000';
                }
            }
            return `style=${quote}${newStyle}${quote}`;
        });

        // Replace ALL color attributes with black (e.g., <span color="#ffffff"> or color='#ffffff')
        html = html.replace(/color=(["'])([^"']+)\1/gi, (match, quote) => {
            return `color=${quote}#000000${quote}`;
        });

        // Replace ALL colors in style tags with black
        html = html.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, styleContent) => {
            const newStyleContent = styleContent.replace(/color:\s*[^;]+/gi, 'color: #000000');
            return match.replace(styleContent, newStyleContent);
        });

        // Add black color to all text elements that don't have a color specified
        // This ensures ALL pasted text is black
        // Match common text elements: p, span, div, h1-h6, li, td, th, etc.
        html = html.replace(/<(p|span|div|h[1-6]|li|td|th|a|strong|em|b|i|u|s|code|pre|blockquote)([^>]*?)>/gi, (match, tagName, attributes) => {
            // Check if element already has a style attribute with color
            const hasStyleColor = /style=(["'])[^"']*color[^"']*\1/i.test(attributes);
            // Check if element has a color attribute
            const hasColorAttr = /color=(["'])([^"']+)\1/i.test(attributes);
            
            // If element doesn't have a color specified, add black color
            if (!hasStyleColor && !hasColorAttr) {
                // Check if element has a style attribute (without color)
                if (/style=(["'])([^"']*)\1/i.test(attributes)) {
                    // Add color to existing style
                    attributes = attributes.replace(/style=(["'])([^"']*)\1/i, (styleMatch: string, quote: string, styleContent: string) => {
                        // Only add color if it's not already there
                        if (!/color\s*:/i.test(styleContent)) {
                            // Trim trailing semicolons and whitespace before appending
                            const trimmedStyle = styleContent.trim().replace(/;+\s*$/, '');
                            return `style=${quote}${trimmedStyle}; color: #000000${quote}`;
                        }
                        return styleMatch;
                    });
                } else {
                    // Add new style attribute with color
                    attributes = `${attributes} style="color: #000000"`;
                }
            }
            return `<${tagName}${attributes}>`;
        });

        return html;
    };

    // Helper to get valid initial content
    // ProseMirror requires at least one block node in the document
    const getValidContent = (val: string): any => {
        const defaultDoc = { 
            type: 'doc', 
            content: [{ type: 'paragraph' }] // Always ensure at least one paragraph
        };
        
        if (!val || val.trim() === '' || val.trim() === '{}') {
            return defaultDoc;
        }
        try {
            const parsed = JSON.parse(val);
            if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
                // Ensure content array exists and has at least one block node
                if (!parsed.content || !Array.isArray(parsed.content) || parsed.content.length === 0) {
                    return defaultDoc;
                }
                return parsed;
            }
        } catch (e) {
            // Invalid JSON, use default
        }
        return defaultDoc;
    };

    // Deep equality check for JSON objects
    const deepEqual = (obj1: any, obj2: any): boolean => {
        if (obj1 === obj2) return true;
        if (obj1 == null || obj2 == null) return false;
        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;
        
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        
        if (keys1.length !== keys2.length) return false;
        
        for (const key of keys1) {
            if (!keys2.includes(key)) return false;
            if (!deepEqual(obj1[key], obj2[key])) return false;
        }
        
        return true;
    };

    // Helper to safely perform undo - prevents undoing beyond original content
    const safeUndo = (editor: any): boolean => {
        try {
            if (!editor.can().undo()) {
                return false;
            }
            
            // Get current content before undo
            const currentJson = editor.getJSON();
            const originalJson = originalContentRef.current ? JSON.parse(originalContentRef.current) : null;
            
            // If we're already at original content, don't allow undo
            if (originalJson && deepEqual(currentJson, originalJson)) {
                return false;
            }
            
            // Perform undo with error handling for "Position -1" errors
            let redoStateImmediately = false;
            try {
                // Wrap undo in try-catch to handle Position -1 errors
                try {
                    editor.chain().undo().run();
                } catch (undoRunError: any) {
                    // Check if this is a Position -1 error (RangeError with Position -1 message)
                    const isPositionError = undoRunError instanceof RangeError || 
                                          undoRunError?.name === 'RangeError' ||
                                          (undoRunError?.message && (
                                              undoRunError.message.includes('Position -1') || 
                                              undoRunError.message.includes('Invalid position') ||
                                              undoRunError.message.includes('position -1') ||
                                              undoRunError.message.includes('outside of fragment')
                                          ));
                    
                    if (isPositionError) {
                        // Clear corrupted undo history immediately
                        try {
                            const currentContent = editor.getJSON();
                            isSettingContentRef.current = true;
                            editor.commands.setContent(currentContent);
                            setCanUndo(false);
                            setCanRedo(false);
                            return false;
                        } catch (clearError) {
                            console.error('[TiptapEditor] Failed to clear undo history:', clearError);
                            // Try to restore to original as last resort
                            if (originalContentRef.current && editor && !editor.isDestroyed) {
                                try {
                                    isSettingContentRef.current = true;
                                    editor.commands.setContent(JSON.parse(originalContentRef.current));
                                    setCanUndo(false);
                                    setCanRedo(false);
                                } catch (e) {
                                    console.error('[TiptapEditor] Failed to restore original content:', e);
                                }
                            }
                            return false;
                        }
                    }
                    // Re-throw if it's not a position error
                    throw undoRunError;
                }
                
                // Check redo state IMMEDIATELY after undo to preserve it
                // Tiptap might clear it if it thinks we've reached a "base" state
                try {
                    redoStateImmediately = editor.can().redo();
                } catch (redoCheckError: any) {
                    // If checking redo state fails (Position -1 error), clear history
                    const isPositionError = redoCheckError instanceof RangeError || 
                                          redoCheckError?.name === 'RangeError' ||
                                          (redoCheckError?.message && (
                                              redoCheckError.message.includes('Position -1') || 
                                              redoCheckError.message.includes('Invalid position') ||
                                              redoCheckError.message.includes('position -1') ||
                                              redoCheckError.message.includes('outside of fragment')
                                          ));
                    
                    if (isPositionError) {
                        try {
                            const currentContent = editor.getJSON();
                            isSettingContentRef.current = true;
                            editor.commands.setContent(currentContent);
                            setCanUndo(false);
                            setCanRedo(false);
                            return false;
                        } catch (clearError) {
                            console.error('[TiptapEditor] Failed to clear undo history:', clearError);
                            return false;
                        }
                    }
                    // Re-throw if it's not a position error
                    throw redoCheckError;
                }
            } catch (undoError: any) {
                // Check if this is a "Position -1" error (common after paste operations)
                const isPositionError = undoError instanceof RangeError || 
                                      undoError?.name === 'RangeError' ||
                                      (undoError?.message && (
                                          undoError.message.includes('Position -1') || 
                                          undoError.message.includes('Invalid position') ||
                                          undoError.message.includes('position -1') ||
                                          undoError.message.includes('outside of fragment')
                                      ));
                
                if (isPositionError) {
                    // Clear corrupted undo history by resetting content
                    try {
                        const currentContent = editor.getJSON();
                        isSettingContentRef.current = true;
                        editor.commands.setContent(currentContent);
                        setCanUndo(false);
                        setCanRedo(false);
                        return false;
                    } catch (clearError) {
                        console.error('[TiptapEditor] Failed to clear undo history after Position -1 error:', clearError);
                        // Try to restore to original as last resort
                        if (originalContentRef.current && editor && !editor.isDestroyed) {
                            try {
                                isSettingContentRef.current = true;
                                editor.commands.setContent(JSON.parse(originalContentRef.current));
                                setCanUndo(false);
                                setCanRedo(false);
                            } catch (e) {
                                console.error('[TiptapEditor] Failed to restore original content:', e);
                            }
                        }
                        return false;
                    }
                }
                
                // Re-throw if it's not a position error
                throw undoError;
            }
            
            // Get content after undo - use a small delay to ensure undo is complete
            setTimeout(() => {
                if (editor && !editor.isDestroyed) {
                    const afterUndoJson = editor.getJSON();
                    const afterUndoContentString = JSON.stringify(afterUndoJson);
                    
                    // Check if we've reached original content or gone past it
                    const isAtOriginal = originalJson && deepEqual(afterUndoJson, originalJson);
                    const isEmpty = !afterUndoJson?.content || (Array.isArray(afterUndoJson.content) && afterUndoJson.content.length === 0);
                    const isPastOriginal = originalJson && afterUndoContentString.length < (originalContentRef.current?.length || 0) * 0.5;
                    
                    // Update undo/redo button states
                    let canUndoNow = editor.can().undo();
                    let canRedoNow = editor.can().redo();
                    
                    if (isAtOriginal) {
                        // We're at the original - block further undo but preserve redo
                        // Don't call setContent here - it would clear the redo history
                        // Just update the button states to reflect that undo is blocked
                        canUndoNow = false;
                        // Use the redo state we captured immediately after undo
                        // This preserves it even if Tiptap clears it later
                        canRedoNow = redoStateImmediately;
                    } else if (isEmpty || isPastOriginal) {
                        // We've gone past the original (empty/invalid) - restore to original
                        // This will clear history, but it's necessary to restore valid content
                        isSettingContentRef.current = true;
                        editor.commands.setContent(originalJson);
                        canUndoNow = false;
                        canRedoNow = editor.can().redo(); // Check redo after restore
                    }
                    
                    setCanUndo(canUndoNow);
                    setCanRedo(canRedoNow);
                }
            }, 10);
            
            return true;
        } catch (error: any) {
            // Check if this is a Position -1 error (RangeError with Position -1 message)
            const isPositionError = error instanceof RangeError || 
                                  error?.name === 'RangeError' ||
                                  (error?.message && (
                                      error.message.includes('Position -1') || 
                                      error.message.includes('Invalid position') ||
                                      error.message.includes('position -1') ||
                                      error.message.includes('outside of fragment')
                                  ));
            
            if (isPositionError) {
                console.error('[TiptapEditor] Position -1 error during undo (corrupted undo history):', error);
                
                // Try to fix by clearing undo history
                if (editor && !editor.isDestroyed) {
                    try {
                        const currentContent = editor.getJSON();
                        isSettingContentRef.current = true;
                        editor.commands.setContent(currentContent);
                        setCanUndo(false);
                        setCanRedo(false);
                        return false;
                    } catch (clearError) {
                        console.error('[TiptapEditor] Failed to clear undo history:', clearError);
                        
                        // Last resort: restore to original if available
                        if (originalContentRef.current) {
                            try {
                                isSettingContentRef.current = true;
                                editor.commands.setContent(JSON.parse(originalContentRef.current));
                                setCanUndo(false);
                                setCanRedo(false);
                            } catch (e) {
                                console.error('[TiptapEditor] Failed to restore original content:', e);
                            }
                        }
                        return false;
                    }
                }
            } else {
                console.error('[TiptapEditor] Error during undo:', error);
                // If undo fails for other reasons, restore to original if available
                if (originalContentRef.current && editor && !editor.isDestroyed) {
                    try {
                        isSettingContentRef.current = true;
                        editor.commands.setContent(JSON.parse(originalContentRef.current));
                        setCanUndo(false);
                        setCanRedo(false);
                    } catch (e) {
                        console.error('[TiptapEditor] Failed to restore original content:', e);
                    }
                }
            }
            return false;
        }
    };

    // Extract page numbers from headings with data-page attributes
    const extractPageNumbersFromHeadings = async () => {
        if (!editor || !onExtractPageNumbers) return;
        
        setIsExtractingPageNumbers(true);
        
        try {
            const pageNumbers: Array<{ headingText: string; pageNumber: string; level: number; index: number }> = [];
            
            // Get JSON format to extract in document order (same order as handleExtractPageNumbers processes)
            const json = editor.getJSON();
            console.log('[TiptapEditor] extractPageNumbersFromHeadings - Checking JSON for data-page attributes');
            
            // Helper function to extract text from a node
            const extractTextFromNode = (node: any): string => {
                if (!node) return '';
                if (typeof node === 'string') return node;
                if (node.type === 'text') return node.text || '';
                if (node.content && Array.isArray(node.content)) {
                    return node.content.map((child: any) => extractTextFromNode(child)).join('');
                }
                return '';
            };
            
            // Track heading index to match with handleExtractPageNumbers
            let headingIndex = 0;
            
            // Recursively traverse JSON content to find headings in document order
            const traverseNodes = (nodes: any[]) => {
                if (!nodes || !Array.isArray(nodes)) return;
                
                nodes.forEach((node: any) => {
                    if (node.type === 'heading') {
                        const level = node.attrs?.level || 1;
                        const dataPage = node.attrs?.['data-page'] || node.attrs?.dataPage;
                        const rawText = extractTextFromNode(node);
                        const headingText = rawText.trim() || 'Untitled';
                        
                        console.log(`[TiptapEditor] extractPageNumbersFromHeadings - Heading ${headingIndex + 1}:`, {
                            level,
                            text: headingText.substring(0, 50),
                            hasDataPage: !!dataPage,
                            dataPageValue: dataPage,
                            index: headingIndex
                        });
                        
                        if (dataPage) {
                            pageNumbers.push({
                                headingText,
                                pageNumber: String(dataPage).trim(),
                                level,
                                index: headingIndex
                            });
                        }
                        
                        headingIndex++;
                    }
                    
                    // Recursively process child content
                    if (node.content && Array.isArray(node.content)) {
                        traverseNodes(node.content);
                    }
                });
            };
            
            if (json && json.content) {
                traverseNodes(json.content);
            }
            
            console.log('[TiptapEditor] extractPageNumbersFromHeadings - Extracted page numbers:', pageNumbers);
            
            // Call the callback with extracted page numbers
            if (pageNumbers.length > 0) {
                await onExtractPageNumbers(pageNumbers);
            } else {
                // Show a message if no page numbers found
                setInfoDialog({
                    isOpen: true,
                    title: 'No Page Numbers Found',
                    message: 'No page numbers found in headings. Make sure headings have data-page attributes. Check console for details.'
                });
            }
        } catch (error) {
            console.error('[TiptapEditor] Error extracting page numbers:', error);
            setInfoDialog({
                isOpen: true,
                title: 'Error',
                message: 'An error occurred while extracting page numbers. Please check the console for details.'
            });
        } finally {
            setIsExtractingPageNumbers(false);
        }
    };

    // Helper to safely perform redo
    const safeRedo = (editor: any): boolean => {
        try {
            if (!editor.can().redo()) {
                return false;
            }
            
            // Perform redo
            editor.chain().redo().run();
            
            // Update undo/redo button states
            setCanUndo(editor.can().undo());
            setCanRedo(editor.can().redo());
            
            return true;
        } catch (error) {
            console.error('Error during redo:', error);
            return false;
        }
    };

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: false, // Disable default link extension, we'll add it separately
                heading: false, // Disable default heading extension
            }),
            CustomHeading.configure({
                levels: [1, 2, 3, 4, 5, 6], // Explicitly configure heading levels
            }), // Use our custom heading extension that preserves data-page
            TextStyle,
            Color,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            FontFamily,
            Subscript,
            Superscript,
            Link.configure({
                autolink: true,
                openOnClick: true,
                enableClickSelection: true,
                defaultProtocol: 'https',
                protocols: ['http', 'https'],
                HTMLAttributes: {
                    class: 'tiptap-link',
                    target: '_blank',
                    rel: 'noopener noreferrer nofollow',
                },
            }),
            Image,
            Figure,
            ExtraContent,
            FindReplaceHighlight,
            SearchHighlight,
            Annotation,
        ],
        content: getValidContent(value),
        editable: editable,
        onUpdate: ({ editor, transaction }) => {
            // If this is a programmatic setContent, don't call onChange
            // Note: Don't clear isSettingContentRef here - it will be cleared after scroll restoration
            // in the setContent handler to prevent race conditions with markHeadings
            if (isSettingContentRef.current) {
                // Only clear flag if scroll restoration isn't pending (handled in setContent)
                // The flag will be cleared by the scroll restoration callback or immediately if no scroll needed
                if (!editorInitializedRef.current) {
                    editorInitializedRef.current = true;
                }
                setCanUndo(false);
                setCanRedo(false);
                return;
            }
            
            // Skip onChange on the very first update (initial load)
            if (!editorInitializedRef.current) {
                editorInitializedRef.current = true;
                setCanUndo(false);
                setCanRedo(false);
                return;
            }
            
            // This is a user-initiated change - call onChange
            const json = editor.getJSON();
            
            // Log headings with data-page attributes in JSON format
            const checkHeadingsInJSON = (node: any, path: string = '') => {
                if (node.type === 'heading') {
                    const level = node.attrs?.level || 1;
                    const dataPage = node.attrs?.['data-page'] || node.attrs?.dataPage;
                    if (dataPage) {
                        const text = node.content?.map((c: any) => c.text || '').join('') || '';
                        console.log('[TiptapEditor] onUpdate - Found heading in JSON with data-page:', {
                            level,
                            dataPage,
                            text: text.trim(),
                            attrs: node.attrs,
                            path
                        });
                    }
                }
                if (node.content && Array.isArray(node.content)) {
                    node.content.forEach((child: any, index: number) => {
                        checkHeadingsInJSON(child, `${path}.${index}`);
                    });
                }
            };
            if (json && json.content) {
                json.content.forEach((node: any, index: number) => {
                    checkHeadingsInJSON(node, `content[${index}]`);
                });
            }
            
            const contentString = JSON.stringify(json);
            
            // Check if we're at the original content
            const originalJson = originalContentRef.current ? JSON.parse(originalContentRef.current) : null;
            const isAtOriginal = originalJson && deepEqual(json, originalJson);
            
            onChange(contentString);
            
            // Update undo/redo button states
            try {
                let canUndoNow = editor.can().undo();
                let canRedoNow = editor.can().redo();
                
                // If we're at the original content, block further undo but preserve redo
                if (isAtOriginal) {
                    canUndoNow = false;
                    // canRedoNow should already be correct from editor.can().redo()
                }
                
                setCanUndo(canUndoNow);
                setCanRedo(canRedoNow);
            } catch (e) {
                console.error('[TiptapEditor] Error updating undo/redo states:', e);
                setCanUndo(false);
                setCanRedo(false);
            }
        },
        onSelectionUpdate: ({ editor }) => {
            updateCurrentHeading(editor);
            // Update text selection state for annotation button
            const { from, to } = editor.state.selection;
            setHasTextSelection(from !== to);
        },
        editorProps: {
            attributes: {
                class: 'tiptap-editor-content',
                placeholder: placeholder,
            },
            transformPastedHTML: (html: string) => {
                // Log the pasted HTML to see what we're working with
                console.log('[TiptapEditor] transformPastedHTML - Received HTML:', html);
                
                // Extract and log all headings with data-page attributes
                const headingRegex = /<(h[1-6])[^>]*data-page=(["'])([^"']+)\2[^>]*>([\s\S]*?)<\/\1>/gi;
                const headingsWithPageNumbers: Array<{ tag: string; pageNumber: string; text: string }> = [];
                let headingMatch;
                while ((headingMatch = headingRegex.exec(html)) !== null) {
                    const tag = headingMatch[1];
                    const pageNumber = headingMatch[3];
                    const content = headingMatch[4];
                    // Extract text content (remove HTML tags)
                    const textContent = content.replace(/<[^>]*>/g, '').trim();
                    headingsWithPageNumbers.push({
                        tag,
                        pageNumber,
                        text: textContent
                    });
                }
                if (headingsWithPageNumbers.length > 0) {
                    console.log('[TiptapEditor] Found headings with data-page attributes:', headingsWithPageNumbers);
                } else {
                    console.log('[TiptapEditor] No headings with data-page attributes found in pasted HTML');
                    // Also check for any headings at all
                    const anyHeadingRegex = /<(h[1-6])[^>]*>([\s\S]*?)<\/\1>/gi;
                    const allHeadings: Array<{ tag: string; attributes: string; text: string }> = [];
                    let anyMatch;
                    while ((anyMatch = anyHeadingRegex.exec(html)) !== null) {
                        const fullMatch = anyMatch[0];
                        const tag = anyMatch[1];
                        const content = anyMatch[2];
                        const textContent = content.replace(/<[^>]*>/g, '').trim();
                        // Extract attributes
                        const attrMatch = fullMatch.match(/<(h[1-6])([^>]*)>/);
                        const attributes = attrMatch ? attrMatch[2] : '';
                        allHeadings.push({
                            tag,
                            attributes,
                            text: textContent
                        });
                    }
                    if (allHeadings.length > 0) {
                        console.log('[TiptapEditor] Found headings without data-page (showing first 5):', allHeadings.slice(0, 5));
                    }
                }
                
                // Extract and log all image src attributes
                const imgRegex = /<img[^>]*src=(["'])([^"']+)\1[^>]*>/gi;
                const imagePaths: string[] = [];
                let match;
                while ((match = imgRegex.exec(html)) !== null) {
                    if (match[2]) {
                        imagePaths.push(match[2]);
                    }
                }
                if (imagePaths.length > 0) {
                    console.log('[TiptapEditor] Found image paths in pasted HTML:', imagePaths);
                }
                
                // Get pending image replacements from ref
                const pendingReplacements = pendingImageReplacementsRef.current;
                if (pendingReplacements && pendingReplacements.length > 0) {
                    // Create a map for compatibility (though we use array in transformPastedHTML)
                    const replacementMap = new Map<string, string>();
                    pendingReplacements.forEach((base64: string, index: number) => {
                        replacementMap.set(`clipboard-image-${index}`, base64);
                    });
                    // Clear the pending replacements after use
                    pendingImageReplacementsRef.current = [];
                    const transformed = transformPastedHTML(html, replacementMap);
                    
                    // Check if we still have broken images after transformation
                    if (brokenImageRefsRef.current.length > 0) {
                        // Trigger file dialog - use queueMicrotask to keep it in the same event loop
                        queueMicrotask(() => {
                            if (pasteImageInputRef.current && brokenImageRefsRef.current.length > 0) {
                                try {
                                    pasteImageInputRef.current.click();
                                } catch (error) {
                                    console.warn('[TiptapEditor] Failed to trigger file dialog:', error);
                                    // Fallback: try again after a short delay
                                    setTimeout(() => {
                                        if (pasteImageInputRef.current && brokenImageRefsRef.current.length > 0) {
                                            pasteImageInputRef.current.click();
                                        }
                                    }, 50);
                                }
                            }
                        });
                    }
                    
                    // Log transformed HTML to check if data-page attributes are preserved
                    console.log('[TiptapEditor] transformPastedHTML - Transformed HTML (first 2000 chars):', transformed.substring(0, 2000));
                    const transformedHeadingRegex = /<(h[1-6])[^>]*data-page=(["'])([^"']+)\2[^>]*>([\s\S]*?)<\/\1>/gi;
                    const transformedHeadings: Array<{ tag: string; pageNumber: string; text: string }> = [];
                    let transformedMatch;
                    while ((transformedMatch = transformedHeadingRegex.exec(transformed)) !== null) {
                        const tag = transformedMatch[1];
                        const pageNumber = transformedMatch[3];
                        const content = transformedMatch[4];
                        const textContent = content.replace(/<[^>]*>/g, '').trim();
                        transformedHeadings.push({
                            tag,
                            pageNumber,
                            text: textContent
                        });
                    }
                    if (transformedHeadings.length > 0) {
                        console.log('[TiptapEditor] transformPastedHTML - data-page attributes preserved in transformed HTML:', transformedHeadings);
                    } else {
                        console.warn('[TiptapEditor] transformPastedHTML - WARNING: data-page attributes were NOT preserved in transformed HTML!');
                    }
                    
                    return transformed;
                }
                
                const transformed = transformPastedHTML(html);
                
                // Log transformed HTML to check if data-page attributes are preserved
                console.log('[TiptapEditor] transformPastedHTML - Transformed HTML (first 2000 chars):', transformed.substring(0, 2000));
                const transformedHeadingRegex = /<(h[1-6])[^>]*data-page=(["'])([^"']+)\2[^>]*>([\s\S]*?)<\/\1>/gi;
                const transformedHeadings: Array<{ tag: string; pageNumber: string; text: string }> = [];
                let transformedMatch;
                while ((transformedMatch = transformedHeadingRegex.exec(transformed)) !== null) {
                    const tag = transformedMatch[1];
                    const pageNumber = transformedMatch[3];
                    const content = transformedMatch[4];
                    const textContent = content.replace(/<[^>]*>/g, '').trim();
                    transformedHeadings.push({
                        tag,
                        pageNumber,
                        text: textContent
                    });
                }
                if (transformedHeadings.length > 0) {
                    console.log('[TiptapEditor] transformPastedHTML - data-page attributes preserved in transformed HTML:', transformedHeadings);
                } else {
                    console.warn('[TiptapEditor] transformPastedHTML - WARNING: data-page attributes were NOT preserved in transformed HTML!');
                }
                
                // Check if we have broken images and no clipboard replacements
                if (brokenImageRefsRef.current.length > 0) {
                    console.log('[TiptapEditor] Broken images detected:', brokenImageRefsRef.current.map(ref => ({
                        path: ref.originalPath,
                        filename: ref.filename
                    })));
                    
                    // First, try to load from selected folder if available
                    if (imageFolderMapRef.current.size > 0) {
                        console.log('[TiptapEditor] Folder map has', imageFolderMapRef.current.size, 'images, attempting to load...');
                        loadImagesFromFolder();
                    } else {
                        // No folder selected - prompt user to select folder
                        console.log('[TiptapEditor] No folder selected. Triggering directory picker...');
                        // Trigger directory picker - use queueMicrotask to keep it in the same event loop
                        queueMicrotask(() => {
                            if (directoryInputRef.current && brokenImageRefsRef.current.length > 0) {
                                try {
                                    console.log('[TiptapEditor] Clicking directory input...');
                                    directoryInputRef.current.click();
                                } catch (error) {
                                    console.warn('[TiptapEditor] Failed to trigger directory picker:', error);
                                    // Fallback: try again after a short delay
                                    setTimeout(() => {
                                        if (directoryInputRef.current && brokenImageRefsRef.current.length > 0) {
                                            directoryInputRef.current.click();
                                        }
                                    }, 50);
                                }
                            } else {
                                console.warn('[TiptapEditor] Directory input ref not available');
                            }
                        });
                    }
                }
                
                return transformed;
            },
            handlePaste: (view, event, slice) => {
                // Handle paste operations - especially large ones with images
                // Extract images from clipboard and convert to base64 for broken image sources
                
                const clipboardData = event.clipboardData;
                if (!clipboardData) {
                return false; // Let default handler process
                }

                // Check if clipboard contains image files
                const items = Array.from(clipboardData.items);
                const imageFiles: File[] = [];
                
                for (const item of items) {
                    if (item.type.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (file) {
                            imageFiles.push(file);
                        }
                    }
                }

                // Check if pasted HTML contains broken image references (even if no clipboard images)
                // We'll detect this after paste and prompt user to select images
                const pastedText = clipboardData.getData('text/html');
                if (pastedText) {
                    // Store a flag that we need to check for broken images after paste
                    // This will be handled in the post-paste processing
                }

                // If we have image files, convert them to base64 and store for HTML transformation
                if (imageFiles.length > 0) {
                    // Convert all image files to base64 data URLs asynchronously
                    // We'll process them and store in ref for transformPastedHTML to use
                    const base64Promises = imageFiles.map((file) => {
                        return new Promise<string>((resolve) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                const result = e.target?.result as string;
                                if (result) {
                                    resolve(result);
                                } else {
                                    resolve('');
                                }
                            };
                            reader.onerror = () => resolve('');
                            reader.readAsDataURL(file);
                        });
                    });

                    // Process images asynchronously - store them for transformPastedHTML or apply after paste
                    Promise.all(base64Promises).then((base64Images) => {
                        // Store replacements in ref (filter out empty strings)
                        const validImages = base64Images.filter(img => img !== '');
                        pendingImageReplacementsRef.current = validImages;
                        
                        // If paste already happened and we have images, try to replace broken image placeholders
                        if (editor && !editor.isDestroyed && validImages.length > 0) {
                            // Small delay to ensure paste is complete and HTML is transformed
                            setTimeout(() => {
                                const htmlContent = editor.getHTML();
                                
                                // Check if HTML contains broken image placeholders (created by transformPastedHTML)
                                // Look for placeholder spans like: <span style="color: #999; font-style: italic;">[Image: ...]</span>
                                const placeholderRegex = /<span style="color:\s*#999;\s*font-style:\s*italic;">\[Image:[^\]]+\]<\/span>/gi;
                                const placeholders = htmlContent.match(placeholderRegex);
                                
                                if (placeholders && placeholders.length > 0 && validImages.length > 0) {
                                    // Replace placeholders with actual images
                                    let newHtml = htmlContent;
                                    let imageIndex = 0;
                                    
                                    placeholders.forEach((placeholder) => {
                                        if (imageIndex < validImages.length) {
                                            const base64Image = validImages[imageIndex];
                                            // Replace placeholder with an img tag
                                            newHtml = newHtml.replace(placeholder, `<img src="${base64Image}" alt="Pasted image" />`);
                                            imageIndex++;
                                        }
                                    });
                                    
                                    // Update editor content if we made changes
                                    if (newHtml !== htmlContent) {
                                        isSettingContentRef.current = true;
                                        editor.commands.setContent(newHtml);
                                        isSettingContentRef.current = false;
                                    }
                                    
                                    // Clear replacements after use
                                    pendingImageReplacementsRef.current = [];
                                    brokenImageRefsRef.current = [];
                                } else {
                                    // Also check for broken image references that might not have been transformed yet
                                    const brokenImageRegex = /<img[^>]*src=(["'])(file:\/\/|\.\/|\.\.\/|[A-Z]:\\|^\/[^/])[^"']+\1[^>]*>/gi;
                                    if (brokenImageRegex.test(htmlContent)) {
                                        // Re-process the HTML with replacements
                                        const transformedHTML = transformPastedHTML(htmlContent, new Map(
                                            validImages.map((base64, index) => [`clipboard-image-${index}`, base64])
                                        ));
                                        
                                        // Update editor content if transformation changed it
                                        if (transformedHTML !== htmlContent) {
                                            isSettingContentRef.current = true;
                                            editor.commands.setContent(transformedHTML);
                                            isSettingContentRef.current = false;
                                        }
                                        
                                        // Clear replacements after use
                                        pendingImageReplacementsRef.current = [];
                                        brokenImageRefsRef.current = [];
                                    }
                                }
                            }, 150); // Slightly longer delay to ensure paste transformation is complete
                        }
                    }).catch((error) => {
                        console.warn('[TiptapEditor] Failed to process clipboard images:', error);
                        pendingImageReplacementsRef.current = [];
                    });
                } else {
                    // No images in clipboard - the file dialog will be triggered by transformPastedHTML
                    // if broken images are detected
                }

                // Let the default paste handler process
                // The transformPastedHTML will use the stored replacements if available
                return false;
            },
            handleKeyDown: (view, event) => {
                // Intercept Ctrl+Z (undo) and Ctrl+Y/Ctrl+Shift+Z (redo) to add error handling
                const currentEditor = editorRef.current;
                if (!currentEditor) return false;
                
                if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
                    // Ctrl+Z or Cmd+Z (undo)
                    event.preventDefault();
                    try {
                        const success = safeUndo(currentEditor);
                        if (!success) {
                            // If undo would result in invalid state, clear history
                            setCanUndo(false);
                            setCanRedo(false);
                        }
                        return true; // Prevent default to avoid double handling
                    } catch (error) {
                        console.error('Error during undo (keyboard shortcut):', error);
                        // Reset undo/redo state if undo fails to prevent further errors
                        setCanUndo(false);
                        setCanRedo(false);
                        return true; // Prevent default to avoid error propagation
                    }
                } else if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
                    // Ctrl+Y or Ctrl+Shift+Z or Cmd+Y or Cmd+Shift+Z (redo)
                    event.preventDefault();
                    try {
                        const success = safeRedo(currentEditor);
                        if (!success) {
                            // If redo would result in invalid state, clear history
                            setCanUndo(false);
                            setCanRedo(false);
                        }
                        return true; // Prevent default to avoid double handling
                    } catch (error) {
                        console.error('Error during redo (keyboard shortcut):', error);
                        // Reset undo/redo state if redo fails to prevent further errors
                        setCanUndo(false);
                        setCanRedo(false);
                        return true; // Prevent default to avoid error propagation
                    }
                }
                return false; // Let other keys be handled normally
            },
        },
    });

    const updateCurrentHeading = (editor: any) => {
        if (editor.isActive('heading', { level: 1 })) {
            setCurrentHeading('Heading 1');
        } else if (editor.isActive('heading', { level: 2 })) {
            setCurrentHeading('Heading 2');
        } else if (editor.isActive('heading', { level: 3 })) {
            setCurrentHeading('Heading 3');
        } else if (editor.isActive('heading', { level: 4 })) {
            setCurrentHeading('Heading 4');
        } else if (editor.isActive('heading', { level: 5 })) {
            setCurrentHeading('Heading 5');
        } else if (editor.isActive('heading', { level: 6 })) {
            setCurrentHeading('Heading 6');
        } else {
            setCurrentHeading('Normal Text');
        }
    };

    React.useEffect(() => {
        if (!editor) {
            return;
        }

        try {
            let contentToSet: any;
            let contentString: string;

            // Handle empty or invalid values
            if (!value || value.trim() === '' || value.trim() === '{}') {
                contentToSet = { type: 'doc', content: [{ type: 'paragraph' }] };
                contentString = JSON.stringify(contentToSet);
            } else {
                const parsedValue = JSON.parse(value);

                // Validate that it's a valid Tiptap document structure
                if (!parsedValue || typeof parsedValue !== 'object' || parsedValue.type !== 'doc') {
                    contentToSet = { type: 'doc', content: [{ type: 'paragraph' }] };
                    contentString = JSON.stringify(contentToSet);
                } else {
                    // Ensure content array exists and has at least one block node
                    if (!parsedValue.content || !Array.isArray(parsedValue.content) || parsedValue.content.length === 0) {
                        parsedValue.content = [{ type: 'paragraph' }];
                    }
                    contentToSet = parsedValue;
                    contentString = JSON.stringify(parsedValue);
                }
            }

            const currentJson = editor.getJSON();
            const currentJsonString = JSON.stringify(currentJson);

            // Only update if content is different
            if (contentString !== currentJsonString) {
                // Save scroll position before setting content to prevent unwanted scroll to bottom
                // Only preserve scroll if this is not the initial load (editor already has content)
                const scrollContainer = scrollContainerRef.current;
                const isInitialLoad = !originalContentRef.current;
                const savedScrollTop = (!isInitialLoad && scrollContainer) ? scrollContainer.scrollTop : 0;
                
                // Use queueMicrotask to defer setContent when images are present
                // This prevents flushSync warnings by deferring until after React's render cycle
                // Set the flag inside the microtask to avoid race conditions with user input
                queueMicrotask(() => {
                    if (editor && !editor.isDestroyed) {
                        // Set flag right before setContent to prevent race conditions
                        // This ensures user input between flag setting and setContent won't be lost
                        isSettingContentRef.current = true;
                        editor.commands.setContent(contentToSet);
                        
                        // Store as original content ONLY on first load (when originalContentRef is null)
                        if (!originalContentRef.current) {
                            originalContentRef.current = contentString;
                        }
                        
                        setCanUndo(false);
                        setCanRedo(false);
                        
                        // Restore scroll position after content is set (only if not initial load)
                        // Use requestAnimationFrame to ensure DOM is updated
                        // Keep flag true until scroll restoration completes to prevent race conditions
                        if (!isInitialLoad && scrollContainer && savedScrollTop > 0) {
                            requestAnimationFrame(() => {
                                if (scrollContainer) {
                                    scrollContainer.scrollTop = savedScrollTop;
                                }
                                // Clear flag after scroll restoration completes
                                isSettingContentRef.current = false;
                            });
                        } else {
                            // If no scroll restoration needed, clear flag immediately
                            isSettingContentRef.current = false;
                        }
                    }
                });
            }
        } catch (e) {
            // If value is not valid JSON, treat it as empty
            const emptyDoc = { type: 'doc', content: [{ type: 'paragraph' }] };
            const emptyDocString = JSON.stringify(emptyDoc);
            const currentJson = editor.getJSON();
            if (emptyDocString !== JSON.stringify(currentJson)) {
                // Save scroll position before setting content to prevent unwanted scroll to bottom
                // Only preserve scroll if this is not the initial load (editor already has content)
                const scrollContainer = scrollContainerRef.current;
                const isInitialLoad = !originalContentRef.current;
                const savedScrollTop = (!isInitialLoad && scrollContainer) ? scrollContainer.scrollTop : 0;
                
                // Use queueMicrotask to defer setContent
                // Set the flag inside the microtask to avoid race conditions with user input
                queueMicrotask(() => {
                    if (editor && !editor.isDestroyed) {
                        // Set flag right before setContent to prevent race conditions
                        // This ensures user input between flag setting and setContent won't be lost
                        isSettingContentRef.current = true;
                        editor.commands.setContent(emptyDoc);
                        
                        // Store as original content ONLY on first load
                        if (!originalContentRef.current) {
                            originalContentRef.current = emptyDocString;
                        }
                        
                        setCanUndo(false);
                        setCanRedo(false);
                        
                        // Restore scroll position after content is set (only if not initial load)
                        // Use requestAnimationFrame to ensure DOM is updated
                        // Keep flag true until scroll restoration completes to prevent race conditions
                        if (!isInitialLoad && scrollContainer && savedScrollTop > 0) {
                            requestAnimationFrame(() => {
                                if (scrollContainer) {
                                    scrollContainer.scrollTop = savedScrollTop;
                                }
                                // Clear flag after scroll restoration completes
                                isSettingContentRef.current = false;
                            });
                        } else {
                            // If no scroll restoration needed, clear flag immediately
                            isSettingContentRef.current = false;
                        }
                    }
                });
            }
        }
    }, [value, editor]);

    // Update editor editability when prop changes
    useEffect(() => {
        if (editor && !editor.isDestroyed) {
            editor.setEditable(editable);
        }
    }, [editor, editable]);

    // Update search query for highlighting
    // Use a ref to track the previous search query to avoid unnecessary updates
    const prevSearchQueryRef = useRef<string | undefined>(undefined);
    
    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        
        // Only update if search query actually changed
        if (prevSearchQueryRef.current === searchQuery) {
            return;
        }
        prevSearchQueryRef.current = searchQuery;
        
        // Update the global search query state
        if (searchQuery && searchQuery.trim()) {
            setSearchQuery(searchQuery);
        } else {
            clearSearchQuery();
        }
        
    }, [editor, searchQuery]);

    // Mark headings with data-section-id attributes after content is rendered
    useEffect(() => {
        if (!editor || !scrollContainerRef.current || headings.length === 0) return;

        // Track all timeout IDs from handleUpdate to allow cleanup
        const updateTimeoutIds = new Set<number>();

        const markHeadings = () => {
            // Don't mark headings if we're currently setting content (to avoid race conditions)
            if (isSettingContentRef.current) {
                return;
            }
            
            // Don't run if already marking headings (prevents re-entry)
            if (isMarkingHeadingsRef.current) {
                return;
            }
            
            // Safety check: ensure editor is still valid
            if (!editor || editor.isDestroyed || !editor.view?.dom) {
                return;
            }
            
            // Safety check: ensure the editor view is synchronized
            // This prevents issues when transactions are being processed
            try {
                // Check if the view state is valid
                if (!editor.view.state || !editor.view.state.doc) {
                    return;
                }
            } catch (e) {
                console.warn('[TiptapEditor] markHeadings: editor view state check failed:', e);
                return;
            }
            
            isMarkingHeadingsRef.current = true;
            
            try {
                // Preserve scroll position before DOM manipulation
                const scrollContainer = scrollContainerRef.current;
                const savedScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
                
                const editorDOM = editor.view.dom;
                const headingElements = editorDOM.querySelectorAll('h1, h2, h3, h4, h5, h6');
                const headingElementsArray = Array.from(headingElements);
                
                // Match headings by index: DOM heading at index N corresponds to headings array at index N
                // This works because both DOM and headings array are in document order
                headingElementsArray.forEach((headingEl, domIndex) => {
                    const element = headingEl as HTMLElement;
                    
                    // Match by index - DOM index should match headings array index
                    if (domIndex < headings.length) {
                        const heading = headings[domIndex];
                        const currentValue = element.getAttribute('data-section-id');
                        
                        // Set data-section-id if the heading has a sectionId
                        if (heading.sectionId) {
                            if (currentValue !== heading.sectionId) {
                                element.setAttribute('data-section-id', heading.sectionId);
                            }
                        } else {
                            // If heading doesn't have a sectionId, remove the attribute if it exists
                            // This handles the case where a section was deleted or a new heading was added
                            if (element.hasAttribute('data-section-id')) {
                                element.removeAttribute('data-section-id');
                            }
                        }
                    } else {
                        // If DOM has more headings than the headings array, remove attributes from extras
                        // This can happen when a new heading is added but headings haven't been re-extracted yet
                        if (element.hasAttribute('data-section-id')) {
                            element.removeAttribute('data-section-id');
                        }
                    }
                });
                
                // Restore scroll position after DOM updates
                if (scrollContainer && savedScrollTop > 0) {
                    // Use requestAnimationFrame to ensure DOM updates are complete
                    requestAnimationFrame(() => {
                        if (scrollContainer) {
                            scrollContainer.scrollTop = savedScrollTop;
                        }
                    });
                }
            } catch (e) {
                console.warn('[TiptapEditor] markHeadings: error during execution:', e);
            } finally {
                isMarkingHeadingsRef.current = false;
            }
        };

        // Mark headings immediately when headings array changes
        // Also mark after a short delay to ensure DOM is updated
        markHeadings();
        const timeoutId = setTimeout(markHeadings, 200);
        
        // Also mark on content updates (but only if not setting content)
        const handleUpdate = ({ transaction }: { transaction?: any } = {}) => {
            // Skip if this update is from a node view attribute change (like Figure caption)
            // This prevents position conflicts during figure updates
            if (transaction?.getMeta?.('nodeViewUpdate')) {
                return;
            }
            
            // Use a small delay to avoid running during setContent
            const updateTimeoutId = setTimeout(() => {
                updateTimeoutIds.delete(updateTimeoutId as any);
                // Safety check: ensure editor is still valid
                if (!editor || editor.isDestroyed || !editor.view?.dom) {
                    return;
                }
                if (!isSettingContentRef.current && !isMarkingHeadingsRef.current) {
                    markHeadings();
                }
            }, 100); // Increased delay to give node views time to stabilize
            updateTimeoutIds.add(updateTimeoutId as any);
        };
        
        editor.on('update', handleUpdate);
        // Don't run on selectionUpdate - it's not needed for heading markers
        // and reduces unnecessary processing

        return () => {
            clearTimeout(timeoutId);
            // Clear all pending update timeouts
            updateTimeoutIds.forEach(id => clearTimeout(id));
            updateTimeoutIds.clear();
            if (editor && !editor.isDestroyed) {
                editor.off('update', handleUpdate);
            }
        };
    }, [editor, headings]);

    // Add click handler to annotations for editing
    useEffect(() => {
        if (!editor || !scrollContainerRef.current) return;

        const handleAnnotationClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const annotationElement = target.closest('.annotation-tooltip') as HTMLElement;
            
            if (annotationElement) {
                e.preventDefault();
                e.stopPropagation();
                
                // Get the annotation data
                const elaborated = annotationElement.getAttribute('data-annotation') || '';
                const text = annotationElement.textContent || '';
                
                // Store the elaborated text for the dialog
                setAnnotationElaboratedForEdit(elaborated);
                
                // Find the position of this annotation in the editor by searching the document
                let foundPos: number | null = null;
                let foundLength = 0;
                
                editor.state.doc.descendants((node, pos) => {
                    if (foundPos !== null) return false;
                    if (node.isText && node.marks) {
                        const annotationMark = node.marks.find(mark => mark.type.name === 'annotation');
                        if (annotationMark && 
                            annotationMark.attrs.elaborated === elaborated) {
                            // Found matching annotation - use the text from the mark or node
                            const annotationText = annotationMark.attrs.text || node.text || '';
                            if (annotationText === text || text.includes(annotationText) || annotationText.includes(text)) {
                                foundPos = pos;
                                foundLength = annotationText.length || node.text?.length || text.length;
                                return false;
                            }
                        }
                    }
                    return true;
                });
                
                if (foundPos !== null) {
                    // Set selection to this annotation
                    editor.commands.setTextSelection({ from: foundPos, to: foundPos + foundLength });
                    // Open edit dialog with the elaborated text
                    setShowAnnotationDialog(true);
                } else {
                    // Fallback: if we can't find exact position, just open dialog with current selection
                    // This handles edge cases where the annotation might be split across nodes
                    if (editor.isActive('annotation')) {
                        setShowAnnotationDialog(true);
                    }
                }
            }
        };

        const scrollContainer = scrollContainerRef.current;
        scrollContainer.addEventListener('click', handleAnnotationClick, true); // Use capture phase

        return () => {
            scrollContainer.removeEventListener('click', handleAnnotationClick, true);
        };
    }, [editor]);

    // Scroll detection to find visible section
    useEffect(() => {
        if (!scrollContainerRef.current || !onVisibleSectionChange || headings.length === 0) return;

        const findVisibleSection = () => {
            if (!editor || !scrollContainerRef.current) return;

            const scrollContainer = scrollContainerRef.current;
            const editorDOM = editor.view.dom;
            const headingElements = editorDOM.querySelectorAll('h1, h2, h3, h4, h5, h6');
            
            if (headingElements.length === 0) {
                if (lastVisibleSectionIdRef.current !== null) {
                    lastVisibleSectionIdRef.current = null;
                    onVisibleSectionChange(null);
                }
                return;
            }

            const containerRect = scrollContainer.getBoundingClientRect();
            const viewportTop = scrollContainer.scrollTop;
            const viewportBottom = viewportTop + scrollContainer.clientHeight;

            let visibleSectionId: string | null = null;
            let closestHeading: HTMLElement | null = null;
            let closestDistance = Infinity;

            // Find the heading closest to the top of the viewport
            headingElements.forEach((headingEl) => {
                const element = headingEl as HTMLElement;
                const elementRect = element.getBoundingClientRect();
                
                // Calculate element's position relative to scroll container
                // elementRect.top is relative to viewport, containerRect.top is container's position in viewport
                // The element's position in the scrollable content = elementRect.top - containerRect.top + scrollTop
                const elementTopInScroll = elementRect.top - containerRect.top + scrollContainer.scrollTop;
                const elementBottomInScroll = elementTopInScroll + elementRect.height;

                // Check if heading is visible in viewport
                const isInViewport = elementBottomInScroll >= viewportTop && elementTopInScroll <= viewportBottom;
                
                if (isInViewport) {
                    // Heading is visible - prefer the one closest to the top of viewport
                    const distanceFromTop = Math.abs(elementTopInScroll - viewportTop);
                    if (distanceFromTop < closestDistance) {
                        closestDistance = distanceFromTop;
                        closestHeading = element;
                    }
                } else if (elementTopInScroll < viewportTop && elementTopInScroll > viewportTop - 300) {
                    // Heading is just above viewport (within 300px) - use as fallback
                    // This handles cases where user scrolls past a heading
                    const distance = viewportTop - elementTopInScroll;
                    if (distance < closestDistance || closestHeading === null) {
                        closestDistance = distance;
                        closestHeading = element;
                    }
                }
            });

            // If we found a heading, get its sectionId
            if (closestHeading !== null) {
                const headingElement = closestHeading as HTMLElement;
                let sectionIdAttr = headingElement.getAttribute('data-section-id');
                
                // If data-section-id is not set, try to set it now (fallback)
                if (!sectionIdAttr) {
                    const headingArray = Array.from(headingElements) as HTMLElement[];
                    const index = headingArray.indexOf(headingElement);
                    if (index >= 0 && index < headings.length && headings[index].sectionId) {
                        // Set the attribute for future use
                        headingElement.setAttribute('data-section-id', headings[index].sectionId!);
                        sectionIdAttr = headings[index].sectionId!;
                    }
                }
                
                if (sectionIdAttr) {
                    visibleSectionId = sectionIdAttr;
                }
            }

            // Update if section changed - check BEFORE updating lastVisibleSectionIdRef
            if (visibleSectionId !== null && visibleSectionId !== lastVisibleSectionIdRef.current) {
                lastVisibleSectionIdRef.current = visibleSectionId;
                if (onVisibleSectionChange) {
                    try {
                        onVisibleSectionChange(visibleSectionId);
                    } catch (error) {
                        console.error('[TiptapEditor] Error calling onVisibleSectionChange:', error);
                    }
                }
            } else if (visibleSectionId === null && lastVisibleSectionIdRef.current !== null) {
                lastVisibleSectionIdRef.current = null;
                if (onVisibleSectionChange) {
                    onVisibleSectionChange(null);
                }
            }
        };

        const handleScroll = () => {
            // Use requestAnimationFrame for smoother updates
            if (scrollTimeoutRef.current) {
                cancelAnimationFrame(scrollTimeoutRef.current as any);
            }
            scrollTimeoutRef.current = requestAnimationFrame(() => {
                findVisibleSection();
            }) as any;
        };

        const scrollContainer = scrollContainerRef.current;
        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        
        // Also check on initial load and after a delay to ensure DOM is ready
        const initialCheck = setTimeout(() => {
            findVisibleSection();
        }, 300);

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                cancelAnimationFrame(scrollTimeoutRef.current as any);
            }
            clearTimeout(initialCheck);
        };
    }, [editor, headings, onVisibleSectionChange]);

    useEffect(() => {
        if (editor) {
            editorRef.current = editor;
            // Defer state updates to avoid flushSync warnings
            startTransition(() => {
                if (editor && !editor.isDestroyed) {
                    updateCurrentHeading(editor);
                    // Initialize undo/redo state - start with false since there's no history initially
                    setCanUndo(false);
                    setCanRedo(false);
                    // Initialize text selection state
                    const { from, to } = editor.state.selection;
                    setHasTextSelection(from !== to);
                }
            });
            editorInitializedRef.current = false;
            // Reset original content ref when editor is recreated
            originalContentRef.current = null;
            // Reset visible section tracking
            lastVisibleSectionIdRef.current = null;
            // Notify parent that editor is ready - use startTransition to defer state update
            // This prevents flushSync warnings by marking the update as non-urgent
            if (onEditorCreated) {
                startTransition(() => {
                    if (editor && !editor.isDestroyed && onEditorCreated) {
                        onEditorCreated(editor);
                    }
                });
            }
        }
        return () => {
            // Cleanup ref when editor is destroyed
            if (!editor || editor.isDestroyed) {
                editorRef.current = null;
            }
        };
    }, [editor, onEditorCreated]);

    if (!editor) {
        return <div>Loading editor...</div>;
    }

    const addLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt(previousUrl ? 'Edit URL (or leave empty to remove):' : 'Enter URL:');
        
        if (url === null) {
            // User cancelled
            return;
        }
        
        if (url === '') {
            // Remove link if URL is empty
            editor.chain().focus().unsetLink().run();
        } else {
            // Ensure URL has a protocol (defaultProtocol only works for autolink, not manual setLink)
            let finalUrl = url.trim();
            // Check if URL already has a protocol
            if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(finalUrl)) {
                // No protocol found, prepend https://
                finalUrl = `https://${finalUrl}`;
            }
            // Add or update link
            editor.chain().focus().setLink({ href: finalUrl, target: '_blank' }).run();
        }
    };

    const removeLink = () => {
        editor.chain().focus().unsetLink().run();
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const src = e.target?.result as string;
                if (src) {
                    // Use Figure extension for images with captions
                    editor.chain().focus().setFigure({
                        src,
                        alt: file.name,
                        caption: ''
                    }).run();
                }
            };
            reader.readAsDataURL(file);
        }
        // Reset the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle directory selection for automatic image loading
    const handleDirectorySelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        console.log('[TiptapEditor] handleDirectorySelect called with', files?.length || 0, 'files');
        
        if (!files || files.length === 0) {
            console.warn('[TiptapEditor] No files selected in directory');
            if (directoryInputRef.current) {
                directoryInputRef.current.value = '';
            }
            imageFolderMapRef.current.clear();
            return;
        }

        // Build a map of file paths to File objects
        const fileMap = new Map<string, File>();
        const fileArray = Array.from(files);
        
        console.log('[TiptapEditor] Processing', fileArray.length, 'files from directory');
        
        fileArray.forEach((file, index) => {
            // Log first few files to see structure
            if (index < 5) {
                console.log('[TiptapEditor] File', index, ':', {
                    name: file.name,
                    type: file.type,
                    webkitRelativePath: (file as any).webkitRelativePath
                });
            }
            
            if (file.type.startsWith('image/')) {
                // Store by full webkitRelativePath (relative to selected folder)
                const relativePath = (file as any).webkitRelativePath || file.name;
                const normalizedPath = relativePath.replace(/\\/g, '/');
                
                console.log('[TiptapEditor] Mapping image:', normalizedPath, '->', file.name);
                
                // Store by full path
                fileMap.set(normalizedPath, file);
                
                // Also store by filename for fallback matching
                const fileName = file.name.toLowerCase();
                if (!fileMap.has(fileName)) {
                    fileMap.set(fileName, file);
                }
                
                // Also store by just the filename (without path) for better matching
                const justFileName = normalizedPath.split('/').pop()?.toLowerCase() || '';
                if (justFileName && !fileMap.has(justFileName)) {
                    fileMap.set(justFileName, file);
                }
            }
        });

        imageFolderMapRef.current = fileMap;
        console.log('[TiptapEditor] Loaded', fileMap.size, 'images from selected folder');
        console.log('[TiptapEditor] Folder map keys:', Array.from(fileMap.keys()).slice(0, 10), '...');

        // If there are broken images, try to load them now
        if (brokenImageRefsRef.current.length > 0 && editor && !editor.isDestroyed) {
            console.log('[TiptapEditor] Loading', brokenImageRefsRef.current.length, 'broken images from folder...');
            loadImagesFromFolder();
        }

        // Reset the input
        if (directoryInputRef.current) {
            directoryInputRef.current.value = '';
        }
    };

    // Load images from selected folder for broken image references
    const loadImagesFromFolder = () => {
        if (!editor || editor.isDestroyed || brokenImageRefsRef.current.length === 0) {
            console.warn('[TiptapEditor] loadImagesFromFolder: Cannot load - editor destroyed or no broken images');
            return;
        }

        const totalImages = brokenImageRefsRef.current.length;
        console.log('[TiptapEditor] loadImagesFromFolder: Processing', totalImages, 'broken images');
        
        // Show loading state
        setIsLoadingImages(true);
        setLoadingImageProgress({ current: 0, total: totalImages });
        processedImageCountRef.current = 0;
        
        const updateProgress = () => {
            processedImageCountRef.current++;
            const currentCount = processedImageCountRef.current;
            const percentage = totalImages > 0 ? Math.round((currentCount / totalImages) * 100) : 0;
            console.log('[TiptapEditor] updateProgress called:', currentCount, '/', totalImages, `(${percentage}%)`);
            
            // Use flushSync to force immediate state update and prevent batching
            // This ensures the UI updates in real-time as each image is processed
            flushSync(() => {
                const newProgress = { 
                    current: currentCount, 
                    total: totalImages 
                };
                console.log('[TiptapEditor] Setting progress state with flushSync:', newProgress);
                setLoadingImageProgress(newProgress);
            });
            
            if (currentCount >= totalImages) {
                console.log('[TiptapEditor] All images processed, hiding loader');
                // Use setTimeout to ensure state updates are applied
                setTimeout(() => {
                    setIsLoadingImages(false);
                    setLoadingImageProgress({ current: 0, total: 0 });
                    // Clear broken refs after all images are processed
                    brokenImageRefsRef.current = [];
                }, 100);
            }
        };
        
        let hasChanges = false;

        brokenImageRefsRef.current.forEach((brokenRef, index) => {
            const normalizedPath = brokenRef.originalPath.replace(/\\/g, '/');
            console.log('[TiptapEditor] Loading image', index + 1, ':', normalizedPath);
            
            let imageFile = imageFolderMapRef.current.get(normalizedPath) || 
                            imageFolderMapRef.current.get(brokenRef.originalPath);
            
            // If not found by exact path, try to match by filename
            if (!imageFile) {
                const srcFileName = normalizedPath.split('/').pop()?.toLowerCase() || '';
                console.log('[TiptapEditor] Trying filename match for:', srcFileName);
                imageFile = Array.from(imageFolderMapRef.current.values()).find(file => {
                    const fileName = file.name.toLowerCase();
                    const matches = fileName === srcFileName || 
                                   fileName.includes(srcFileName) || 
                                   srcFileName.includes(fileName);
                    if (matches) {
                        console.log('[TiptapEditor] Found match:', fileName, 'for', srcFileName);
                    }
                    return matches;
                }) || undefined;
            }

            if (imageFile) {
                console.log('[TiptapEditor] Found file for', normalizedPath, ':', imageFile.name);
                console.log('[TiptapEditor] Placeholder to replace:', brokenRef.placeholder);
                
                // Convert to base64 and replace placeholder
                const reader = new FileReader();
                reader.onload = (e) => {
                    const base64 = e.target?.result as string;
                    if (base64) {
                        console.log('[TiptapEditor] Image loaded, base64 length:', base64.length);
                        // Use a small delay to ensure editor has finished processing
                        setTimeout(() => {
                            if (editor && !editor.isDestroyed) {
                                const currentHtml = editor.getHTML();
                                console.log('[TiptapEditor] Current HTML contains placeholder?', currentHtml.includes(brokenRef.placeholder));
                                
                                // Try exact match first
                                let updatedHtml = currentHtml.replace(brokenRef.placeholder, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                
                                // If exact match didn't work, try pattern matching
                                if (updatedHtml === currentHtml) {
                                    console.log('[TiptapEditor] Exact match failed, trying pattern match...');
                                    // Look for placeholder pattern with the filename
                                    const escapedFilename = brokenRef.filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                    const placeholderPattern = new RegExp(`<span[^>]*>\\[Image:[^\\]]*${escapedFilename}[^\\]]*\\]</span>`, 'gi');
                                    updatedHtml = currentHtml.replace(placeholderPattern, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                }
                                
                                // If still no match, try any placeholder with the filename in the text
                                if (updatedHtml === currentHtml) {
                                    console.log('[TiptapEditor] Pattern match failed, trying any placeholder with filename...');
                                    const escapedFilename = brokenRef.filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                    const placeholderPattern = new RegExp(`<span[^>]*style="color:\\s*#999;\\s*font-style:\\s*italic;">\\[Image:[^\\]]*${escapedFilename}[^\\]]*\\]</span>`, 'gi');
                                    updatedHtml = currentHtml.replace(placeholderPattern, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                }
                                
                                // If still no match, try any placeholder (replace first one found)
                                if (updatedHtml === currentHtml) {
                                    console.log('[TiptapEditor] Filename match failed, trying any placeholder...');
                                    // Try the original pattern first
                                    const anyPlaceholderPattern = /<span[^>]*style="color:\s*#999;\s*font-style:\s*italic;">\[Image:[^\]]+\]<\/span>/gi;
                                    let matches = currentHtml.match(anyPlaceholderPattern);
                                    if (matches && matches.length > 0) {
                                        console.log('[TiptapEditor] Found', matches.length, 'placeholder(s) with original style, replacing first one');
                                        updatedHtml = currentHtml.replace(anyPlaceholderPattern, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                    } else {
                                        // Try transformed placeholder (color changed to black, wrapped in em tags)
                                        // Pattern: <span style="color: rgb(0, 0, 0);"><em>[Image: ...]</em></span>
                                        const transformedPattern = /<span[^>]*>\s*<em>\[Image:[^\]]+\]<\/em>\s*<\/span>/gi;
                                        matches = currentHtml.match(transformedPattern);
                                        if (matches && matches.length > 0) {
                                            console.log('[TiptapEditor] Found', matches.length, 'transformed placeholder(s) with em tags, replacing first one');
                                            updatedHtml = currentHtml.replace(transformedPattern, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                        } else {
                                            // Try without em tags but with transformed color
                                            const transformedPattern2 = /<span[^>]*style="[^"]*color:\s*rgb\(0,\s*0,\s*0\)[^"]*">\s*\[Image:[^\]]+\]\s*<\/span>/gi;
                                            matches = currentHtml.match(transformedPattern2);
                                            if (matches && matches.length > 0) {
                                                console.log('[TiptapEditor] Found', matches.length, 'transformed placeholder(s) with rgb color, replacing first one');
                                                updatedHtml = currentHtml.replace(transformedPattern2, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                            } else {
                                                // Try matching the exact structure from the log: <span style="color: rgb(0, 0, 0);"><em>[Image: broken reference]</em></span>
                                                const exactPattern = /<span[^>]*style="[^"]*color:\s*rgb\(0,\s*0,\s*0\)[^"]*">\s*<em>\[Image:[^\]]+\]<\/em>\s*<\/span>/gi;
                                                matches = currentHtml.match(exactPattern);
                                                if (matches && matches.length > 0) {
                                                    console.log('[TiptapEditor] Found', matches.length, 'exact transformed placeholder(s), replacing first one');
                                                    updatedHtml = currentHtml.replace(exactPattern, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                                } else {
                                                    // Try even simpler - just look for [Image: ...] in any span (non-greedy)
                                                    const simplePattern = /<span[^>]*>[^<]*\[Image:[^\]]+\][^<]*<\/span>/gi;
                                                    matches = currentHtml.match(simplePattern);
                                                    if (matches && matches.length > 0) {
                                                        console.log('[TiptapEditor] Found', matches.length, 'simple placeholder(s), replacing first one');
                                                        updatedHtml = currentHtml.replace(simplePattern, `<img src="${base64}" alt="${brokenRef.filename}" />`);
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                
                                // Instead of replacing HTML, find the placeholder in the ProseMirror document and replace with Figure
                                // Find the placeholder text "[Image: ...]" in the document
                                let foundPosition: number | null = null;
                                let foundLength: number = 0;
                                
                                // Search through the document for "[Image:" text
                                editor.state.doc.descendants((node, pos) => {
                                    if (foundPosition !== null) return false; // Already found
                                    
                                    if (node.isText && node.text) {
                                        const text = node.text;
                                        // Look for "[Image:" in the text
                                        const imageIndex = text.indexOf('[Image:');
                                        if (imageIndex >= 0) {
                                            // Find the end of the placeholder - look for the closing bracket
                                            let endIndex = text.indexOf(']', imageIndex);
                                            if (endIndex < 0) {
                                                // If not found in this node, the placeholder might span multiple nodes
                                                // Just use the rest of this text node
                                                endIndex = text.length;
                                            }
                                            foundPosition = pos + imageIndex;
                                            foundLength = endIndex - imageIndex + 1;
                                            
                                            // Try to find the complete placeholder by looking ahead
                                            // The placeholder format is "[Image: ...]" so we need to find the closing bracket
                                            if (endIndex < text.length - 1) {
                                                // Found complete placeholder in this node
                                                return false; // Stop searching
                                            } else {
                                                // Placeholder might continue in next node, but we'll use what we have
                                                return false; // Stop searching
                                            }
                                        }
                                    }
                                    return true; // Continue searching
                                });
                                
                                if (foundPosition !== null) {
                                    console.log('[TiptapEditor] Found placeholder at position', foundPosition, 'length', foundLength);
                                    try {
                                        // Set selection to the placeholder text
                                        editor.commands.setTextSelection({ from: foundPosition, to: foundPosition + foundLength });
                                        
                                        // Replace with Figure
                                        editor.chain()
                                            .focus()
                                            .deleteSelection()
                                            .setFigure({
                                                src: base64,
                                                alt: brokenRef.filename,
                                                caption: ''
                                        })
                                        .run();
                                    console.log('[TiptapEditor] Replaced placeholder with Figure node');
                                    
                                    // Update progress
                                    updateProgress();
                                    } catch (error) {
                                        console.error('[TiptapEditor] Error replacing placeholder with Figure:', error);
                                        // Fall back to HTML replacement
                                        if (updatedHtml !== currentHtml) {
                                            isSettingContentRef.current = true;
                                            editor.commands.setContent(updatedHtml);
                                            isSettingContentRef.current = false;
                                        }
                                        
                                        // Update progress even on error
                                        updateProgress();
                                    }
                                } else {
                                    console.warn('[TiptapEditor] Could not find placeholder in ProseMirror document, falling back to HTML replacement');
                                    // Fallback to HTML replacement
                                    if (updatedHtml !== currentHtml) {
                                        console.log('[TiptapEditor] HTML updated successfully');
                                        isSettingContentRef.current = true;
                                        editor.commands.setContent(updatedHtml);
                                        isSettingContentRef.current = false;
                                        // Update progress after successful HTML replacement
                                        updateProgress();
                                    } else {
                                        console.warn('[TiptapEditor] Failed to replace placeholder in HTML');
                                        console.log('[TiptapEditor] Placeholder to find:', brokenRef.placeholder);
                                        console.log('[TiptapEditor] Looking for filename:', brokenRef.filename);
                                        // Log a snippet of HTML that might contain the placeholder
                                        const placeholderIndex = currentHtml.indexOf('[Image:');
                                        if (placeholderIndex >= 0) {
                                            console.log('[TiptapEditor] Found [Image: at index', placeholderIndex);
                                            console.log('[TiptapEditor] HTML snippet around placeholder:', currentHtml.substring(Math.max(0, placeholderIndex - 100), placeholderIndex + 200));
                                        } else {
                                            console.log('[TiptapEditor] No [Image: found in HTML');
                                        }
                                        // Update progress even if replacement failed
                                        updateProgress();
                                    }
                                }
                            }
                        }, 100); // Small delay to ensure editor is ready
                    } else {
                        console.warn('[TiptapEditor] Failed to load image as base64');
                        // Update progress when base64 conversion fails
                        updateProgress();
                    }
                };
                reader.onerror = (error) => {
                    console.error('[TiptapEditor] Error reading file:', error);
                    // Update progress on error
                    updateProgress();
                };
                reader.readAsDataURL(imageFile);
                hasChanges = true;
            } else {
                console.warn('[TiptapEditor] No file found for', normalizedPath);
                // Update progress for unmatched images
                updateProgress();
            }
        });

        if (hasChanges) {
            // Clear broken refs after processing (but don't clear if still loading)
            // They'll be cleared when all images are processed
        }
    };

    // Handle image selection after paste for broken image references
    const handlePasteImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0 || !editor || editor.isDestroyed) {
            // Reset the input
            if (pasteImageInputRef.current) {
                pasteImageInputRef.current.value = '';
            }
            brokenImageRefsRef.current = [];
            return;
        }

        const fileArray = Array.from(files);
        const brokenRefs = brokenImageRefsRef.current;
        
        // Convert selected files to base64
        const base64Promises = fileArray.map((file) => {
            return new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const result = e.target?.result as string;
                    resolve(result || '');
                };
                reader.onerror = () => resolve('');
                reader.readAsDataURL(file);
            });
        });

        Promise.all(base64Promises).then((base64Images) => {
            const validImages = base64Images.filter(img => img !== '');
            
            if (validImages.length === 0 || brokenRefs.length === 0) {
                brokenImageRefsRef.current = [];
                if (pasteImageInputRef.current) {
                    pasteImageInputRef.current.value = '';
                }
                return;
            }

            // Get current HTML content
            const htmlContent = editor.getHTML();
            let newHtml = htmlContent;
            
            // Replace placeholders with actual images
            // Match images by index (first broken image -> first selected file)
            // Note: Use pattern matching instead of exact string match because placeholders
            // get transformed (color changed from #999 to #000000 or rgb(0,0,0))
            brokenRefs.forEach((brokenRef, index) => {
                if (index < validImages.length) {
                    const base64Image = validImages[index];
                    // Try multiple patterns to match transformed placeholders
                    // Pattern 1: Original placeholder with #999 color
                    let replaced = newHtml.replace(brokenRef.placeholder, `<img src="${base64Image}" alt="${brokenRef.filename}" />`);
                    
                    // Pattern 2: Transformed placeholder with em tags and rgb color
                    if (replaced === newHtml) {
                        const transformedPattern = /<span[^>]*>\s*<em>\[Image:[^\]]+\]<\/em>\s*<\/span>/gi;
                        replaced = newHtml.replace(transformedPattern, `<img src="${base64Image}" alt="${brokenRef.filename}" />`);
                    }
                    
                    // Pattern 3: Transformed placeholder with rgb(0, 0, 0) color
                    if (replaced === newHtml) {
                        const rgbPattern = /<span[^>]*style="[^"]*color:\s*rgb\(0,\s*0,\s*0\)[^"]*">\s*<em>\[Image:[^\]]+\]<\/em>\s*<\/span>/gi;
                        replaced = newHtml.replace(rgbPattern, `<img src="${base64Image}" alt="${brokenRef.filename}" />`);
                    }
                    
                    // Pattern 4: Any placeholder with [Image: ...] text
                    if (replaced === newHtml) {
                        const anyPlaceholderPattern = /<span[^>]*>[^<]*\[Image:[^\]]+\][^<]*<\/span>/gi;
                        replaced = newHtml.replace(anyPlaceholderPattern, `<img src="${base64Image}" alt="${brokenRef.filename}" />`);
                    }
                    
                    newHtml = replaced;
                }
            });

            // Update editor content if we made changes
            if (newHtml !== htmlContent) {
                isSettingContentRef.current = true;
                editor.commands.setContent(newHtml);
                isSettingContentRef.current = false;
            }

            // Clear refs
            brokenImageRefsRef.current = [];
            if (pasteImageInputRef.current) {
                pasteImageInputRef.current.value = '';
            }
        }).catch((error) => {
            console.error('[TiptapEditor] Failed to process paste images:', error);
            brokenImageRefsRef.current = [];
            if (pasteImageInputRef.current) {
                pasteImageInputRef.current.value = '';
            }
        });
    };

    const addImage = () => {
        // Insert a placeholder figure (no src) that can be uploaded later
        editor.chain().focus().setFigure({
            src: '',
            alt: '',
            caption: ''
        }).run();
    };

    const addExtraContent = () => {
        // Insert the extra content and immediately add a new paragraph
        editor.chain()
            .focus()
            .setExtraContent()
            .command(({ tr, state }) => {
                // Insert a new paragraph at the end of the document
                const endPos = tr.doc.content.size;
                const paragraph = state.schema.nodes.paragraph.create();
                tr.insert(endPos, paragraph);
                return true;
            })
            .run();
    };

    const handleAddAnnotation = () => {
        const { from, to } = editor.state.selection;
        if (from === to) {
            // Check if cursor is inside an annotation (for editing)
            if (editor.isActive('annotation')) {
                // Get existing elaborated text
                const elaborated = getExistingAnnotationElaborated();
                setAnnotationElaboratedForEdit(elaborated);
                setShowAnnotationDialog(true);
            }
            return;
        }
        
        // Clear any existing elaborated text for new annotation
        setAnnotationElaboratedForEdit('');
        setShowAnnotationDialog(true);
    };

    const handleAnnotationSave = (elaborated: string) => {
        const { from, to } = editor.state.selection;
        
        // Check if we're editing an existing annotation
        const isEditing = editor.isActive('annotation');
        
        if (isEditing) {
            // Editing: remove old annotation and apply new one
            const selectedText = from === to 
                ? editor.state.doc.textBetween(from - 1, from, ' ') || editor.state.doc.textBetween(from, from + 1, ' ')
                : editor.state.doc.textBetween(from, to, ' ');
            
            // First unset the existing annotation
            editor.chain()
                .focus()
                .unsetAnnotation()
                .setAnnotation({
                    text: selectedText || getSelectedText(),
                    elaborated: elaborated,
                })
                .run();
        } else {
            // Creating new annotation
            if (from === to) {
                return;
            }
            
            // Get the selected text to use as the annotation label
            const selectedText = editor.state.doc.textBetween(from, to, ' ');
            
            // Apply annotation mark to selected text
            editor.chain()
                .focus()
                .setAnnotation({
                    text: selectedText,
                    elaborated: elaborated,
                })
                .run();
        }
        
        setShowAnnotationDialog(false);
    };

    const handleAnnotationCancel = () => {
        setShowAnnotationDialog(false);
        setAnnotationElaboratedForEdit(''); // Clear when canceling
    };

    const handleAnnotationRemove = () => {
        const { from, to } = editor.state.selection;
        
        // Check if we're in an annotation
        if (editor.isActive('annotation')) {
            // Remove the annotation mark
            editor.chain()
                .focus()
                .unsetAnnotation()
                .run();
        } else if (from !== to) {
            // If text is selected, try to remove annotation from selection
            editor.chain()
                .focus()
                .unsetAnnotation()
                .run();
        }
        
        setShowAnnotationDialog(false);
        setAnnotationElaboratedForEdit('');
    };

    // Check if annotation button should be enabled (text must be selected)
    // Use state variable that's updated on selection changes
    const isAnnotationEnabled = hasTextSelection;

    // Get selected text for annotation dialog
    const getSelectedText = () => {
        const { from, to } = editor.state.selection;
        if (from === to) {
            // If cursor is in an annotation, try to get the annotation text
            if (editor.isActive('annotation')) {
                // Find the annotation mark and get its text attribute
                const resolvedPos = editor.state.doc.resolve(from);
                const marks = resolvedPos.marks();
                const annotationMark = marks.find(mark => mark.type.name === 'annotation');
                if (annotationMark && annotationMark.attrs.text) {
                    return annotationMark.attrs.text;
                }
                // Fallback: try to get text around cursor
                const start = Math.max(0, from - 50);
                const end = Math.min(editor.state.doc.content.size, from + 50);
                return editor.state.doc.textBetween(start, end, ' ').trim();
            }
            return '';
        }
        return editor.state.doc.textBetween(from, to, ' ');
    };

    // Get selected HTML content for annotation dialog
    const getSelectedHtml = () => {
        const { from, to } = editor.state.selection;
        if (from === to) {
            // If cursor is in an annotation, try to get the annotation text as HTML
            if (editor.isActive('annotation')) {
                // Find the annotation mark and get its text attribute
                const resolvedPos = editor.state.doc.resolve(from);
                const marks = resolvedPos.marks();
                const annotationMark = marks.find(mark => mark.type.name === 'annotation');
                if (annotationMark && annotationMark.attrs.text) {
                    return annotationMark.attrs.text;
                }
                return '';
            }
            return '';
        }
        
        // Get HTML for the selected range using ProseMirror's DOMSerializer
        try {
            // Create a slice of the selected content
            const slice = editor.state.doc.slice(from, to);
            if (!slice || !slice.content) {
                return editor.state.doc.textBetween(from, to, ' ');
            }
            
            // Use ProseMirror's DOMSerializer to convert slice to HTML
            const domSerializer = DOMSerializer.fromSchema(editor.state.schema);
            const fragment = domSerializer.serializeFragment(slice.content);
            
            // Convert fragment to HTML string
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(fragment.cloneNode(true));
            return tempDiv.innerHTML;
        } catch (e) {
            // Fallback to plain text if HTML serialization fails
            console.warn('Failed to serialize selection to HTML:', e);
            return editor.state.doc.textBetween(from, to, ' ');
        }
    };

    // Check if cursor is in an annotation
    const isAnnotationActive = () => {
        return editor.isActive('annotation');
    };

    // Get existing annotation elaborated text if editing
    const getExistingAnnotationElaborated = () => {
        const { from } = editor.state.selection;
        const resolvedPos = editor.state.doc.resolve(from);
        const marks = resolvedPos.marks();
        const annotationMark = marks.find(mark => mark.type.name === 'annotation');
        if (annotationMark && annotationMark.attrs.elaborated) {
            return annotationMark.attrs.elaborated;
        }
        return '';
    };

    const scrollToTop = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div
            className={`tiptap-editor-container ${className}`}
            style={{ ...style, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%' }}
        >
            {editable && (
            <div className="tiptap-toolbar" style={{ flexShrink: 0 }}>
                {/* Headers */}
                <select
                    value={
                        currentHeading === 'Normal Text' ? '0' :
                            currentHeading === 'Heading 1' ? '1' :
                                currentHeading === 'Heading 2' ? '2' :
                                    currentHeading === 'Heading 3' ? '3' :
                                        currentHeading === 'Heading 4' ? '4' :
                                            currentHeading === 'Heading 5' ? '5' :
                                                currentHeading === 'Heading 6' ? '6' : '0'
                    }
                    onChange={(e) => {
                        const level = parseInt(e.target.value);
                        if (level === 0) {
                            editor.chain().focus().setParagraph().run();
                        } else {
                            editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 | 4 | 5 | 6 }).run();
                        }
                    }}
                    className="tiptap-select"
                    title="Text Format"
                    disabled={!editable}
                >
                    <option value="0">Normal Text</option>
                    <option value="1">Heading 1</option>
                    <option value="2">Heading 2</option>
                    <option value="3">Heading 3</option>
                    <option value="4">Heading 4</option>
                    <option value="5">Heading 5</option>
                    <option value="6">Heading 6</option>
                </select>

                {/* Basic formatting */}
                <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={editor.isActive('bold') ? 'is-active' : ''}
                    title="Bold"
                >
                    <strong>B</strong>
                </button>

                <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={editor.isActive('italic') ? 'is-active' : ''}
                    title="Italic"
                >
                    <em>I</em>
                </button>

                <button
                    onClick={() => {
                        editor.chain().focus().toggleUnderline().run();
                    }}
                    className={editor.isActive('underline') ? 'is-active' : ''}
                    title="Underline"
                >
                    <u>U</u>
                </button>

                <button
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    className={editor.isActive('strike') ? 'is-active' : ''}
                    title="Strikethrough"
                >
                    <s>S</s>
                </button>

                <div className="tiptap-separator"></div>

                {/* Lists */}
                <button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={editor.isActive('bulletList') ? 'is-active' : ''}
                    title="Bullet List"
                >
                    •
                </button>

                <button
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={editor.isActive('orderedList') ? 'is-active' : ''}
                    title="Numbered List"
                >
                    1.
                </button>

                <div className="tiptap-separator"></div>

                {/* Subscript/Superscript */}
                {/* <button
                    onClick={() => editor.chain().focus().toggleSubscript().run()}
                    className={editor.isActive('subscript') ? 'is-active' : ''}
                    title="Subscript"
                >
                    X₂
                </button>

                <button
                    onClick={() => editor.chain().focus().toggleSuperscript().run()}
                    className={editor.isActive('superscript') ? 'is-active' : ''}
                    title="Superscript"
                >
                    X²
                </button>

                <div className="tiptap-separator"></div> */}

                {/* Indent */}
                <button
                    onClick={() => editor.chain().focus().liftListItem('listItem').run()}
                    title="Decrease Indent"
                >
                    ⬅
                </button>

                <button
                    onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
                    title="Increase Indent"
                >
                    ➡
                </button>

                <div className="tiptap-separator"></div>

                {/* Text Color */}
                <input
                    type="color"
                    onInput={(e) => editor.chain().focus().setColor((e.target as HTMLInputElement).value).run()}
                    value={editor.getAttributes('textStyle').color || '#000000'}
                    title="Text Color"
                    className="tiptap-color-input"
                />

                <div className="tiptap-separator"></div>

                {/* Alignment */}
                <button
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
                    title="Align Left"
                >
                    ⬅
                </button>

                <button
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
                    title="Align Center"
                >
                    ⬌
                </button>

                <button
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
                    title="Align Right"
                >
                    ➡
                </button>

                <div className="tiptap-separator"></div>

                {/* Find and Replace */}
                {onFindReplace && (
                    <button onClick={onFindReplace} title="Find and Replace (Ctrl+F or Cmd+F)">
                        <Search size={16} />
                    </button>
                )}

                {/* Links and Images */}
                <button 
                    onClick={addLink} 
                    title={editor.getAttributes('link').href ? 'Edit Link' : 'Add Link'}
                    className={editor.isActive('link') ? 'is-active' : ''}
                >
                    <LinkIcon size={16} />
                </button>
                {editor.isActive('link') && (
                    <button onClick={removeLink} title="Remove Link">
                        <Link2Off size={16} />
                    </button>
                )}

                <button onClick={addImage} title="Upload Image">
                    🖼
                </button>

                <div className="tiptap-separator"></div>

                {/* Extra Content */}
                <button onClick={addExtraContent} title="Add Extra Content Box">
                    📝
                </button>

                {/* Annotation */}
                <button
                    onClick={handleAddAnnotation}
                    disabled={!isAnnotationEnabled}
                    className={isAnnotationActive() ? 'is-active' : ''}
                    title="Add Annotation (select text first)"
                >
                    📌
                </button>

                {/* Extract Page Numbers - Only for admin and superadmin */}
                {onExtractPageNumbers && isAdminOrSuperadmin && (
                    <button 
                        onClick={extractPageNumbersFromHeadings} 
                        title="Extract page numbers from headings and add to section metadata"
                    >
                        <Pickaxe size={16} />
                    </button>
                )}


                <div className="tiptap-separator"></div>

                {/* Clear formatting */}
                <button
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                    title="Clear Formatting"
                >
                    ✗
                </button>

                {/* Blockquote */}
                <button
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    className={editor.isActive('blockquote') ? 'is-active' : ''}
                    title="Blockquote"
                >
                    "
                </button>

                {/* Code block */}
                <button
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    className={editor.isActive('codeBlock') ? 'is-active' : ''}
                    title="Code Block"
                >
                    &lt;/&gt;
                </button>

                <div className="tiptap-separator"></div>

                {/* Undo/Redo */}
                <button
                    onClick={() => {
                        try {
                            const success = safeUndo(editor);
                            if (!success) {
                                // If undo would result in invalid state, clear history
                                setCanUndo(false);
                                setCanRedo(false);
                            }
                        } catch (error) {
                            console.error('Error during undo:', error);
                            // Reset undo/redo state if undo fails to prevent further errors
                            setCanUndo(false);
                            setCanRedo(false);
                        }
                    }}
                    disabled={!canUndo}
                    title="Undo (Ctrl+Z)"
                    className={!canUndo ? 'disabled' : ''}
                >
                    <Undo size={16} />
                </button>

                <button
                    onClick={() => {
                        try {
                            const success = safeRedo(editor);
                            if (!success) {
                                // If redo would result in invalid state, clear history
                                setCanUndo(false);
                                setCanRedo(false);
                            }
                        } catch (error) {
                            console.error('Error during redo:', error);
                            // Reset undo/redo state if redo fails to prevent further errors
                            setCanUndo(false);
                            setCanRedo(false);
                        }
                    }}
                    disabled={!canRedo}
                    title="Redo (Ctrl+Y)"
                    className={!canRedo ? 'disabled' : ''}
                >
                    <Redo size={16} />
                </button>
            </div>
            )}

            {/* Make the editor content scrollable and fill available space */}
            <div
                ref={scrollContainerRef}
                style={{ flex: 1, overflow: 'auto', background: 'white', minHeight: 0 }}
            >
                <EditorContent
                    editor={editor}
                    className="tiptap-content"
                />
                <BubbleMenu 
                    editor={editor} 
                    options={{ duration: 100 }}
                    className="tiptap-bubble-menu"
                >
                    <div className="bubble-menu-container">
                        <button
                            onClick={() => editor.chain().focus().setParagraph().run()}
                            className={!editor.isActive('heading') ? 'is-active' : ''}
                            title="Normal Text"
                        >
                            Normal
                        </button>
                        <button
                            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                            className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                            title="Heading 1"
                        >
                            H1
                        </button>
                        <button
                            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                            className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                            title="Heading 2"
                        >
                            H2
                        </button>
                        <button
                            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                            className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                            title="Heading 3"
                        >
                            H3
                        </button>
                        <button
                            onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
                            className={editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}
                            title="Heading 4"
                        >
                            H4
                        </button>
                        <button
                            onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
                            className={editor.isActive('heading', { level: 5 }) ? 'is-active' : ''}
                            title="Heading 5"
                        >
                            H5
                        </button>
                        <button
                            onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
                            className={editor.isActive('heading', { level: 6 }) ? 'is-active' : ''}
                            title="Heading 6"
                        >
                            H6
                        </button>
                        <div className="tiptap-separator" style={{ width: '1px', height: '20px', backgroundColor: '#ddd', margin: '0 4px' }}></div>
                        <button
                            onClick={handleAddAnnotation}
                            className={isAnnotationActive() ? 'is-active' : ''}
                            title={isAnnotationActive() ? "Edit Annotation" : "Add Annotation"}
                        >
                            📌
                        </button>
                    </div>
                </BubbleMenu>
            </div>

            {/* Hidden file input for image uploads */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                style={{ display: 'none' }}
            />

            {/* Hidden file input for paste image uploads (multiple files) */}
            <input
                type="file"
                ref={pasteImageInputRef}
                onChange={handlePasteImageUpload}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
            />

            {/* Hidden directory input for selecting image folder */}
            <input
                type="file"
                ref={directoryInputRef}
                onChange={handleDirectorySelect}
                // @ts-ignore - webkitdirectory is a valid HTML attribute but not in TypeScript types
                webkitdirectory=""
                // @ts-ignore - directory is a valid HTML attribute but not in TypeScript types
                directory=""
                style={{ display: 'none' }}
            />

            {/* Scroll to top button */}
            <button
                onClick={scrollToTop}
                style={{
                    position: 'absolute',
                    bottom: '20px',
                    right: '20px',
                    width: '35px',
                    height: '35px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    fontSize: '15px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.3s ease',
                    zIndex: 1000
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                    e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Scroll to top"
            >
                ↑
            </button>

            {/* Annotation Tooltip (Radix UI) */}
            {editor && <AnnotationTooltip editor={editor} />}

            {/* Annotation Dialog */}
            <AnnotationDialog
                isOpen={showAnnotationDialog}
                selectedText={getSelectedText()}
                selectedHtml={getSelectedHtml()}
                onClose={handleAnnotationCancel}
                onSave={handleAnnotationSave}
                onRemove={annotationElaboratedForEdit || getExistingAnnotationElaborated() ? handleAnnotationRemove : undefined}
                existingElaborated={annotationElaboratedForEdit || getExistingAnnotationElaborated()}
            />

            {/* Image Loading Overlay */}
            {isLoadingImages && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        backdropFilter: 'blur(2px)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '16px',
                        }}
                    >
                        {/* Spinner */}
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                border: '4px solid #f3f3f3',
                                borderTop: '4px solid #3498db',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                            }}
                        />
                        {/* Progress text */}
                        <div
                            style={{
                                fontSize: '14px',
                                color: '#666',
                                fontWeight: '500',
                            }}
                        >
                            Loading images... {loadingImageProgress.current} / {loadingImageProgress.total}
                            {loadingImageProgress.total > 0 && (
                                <span style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }}>
                                    ({Math.round((loadingImageProgress.current / loadingImageProgress.total) * 100)}%)
                                </span>
                            )}
                        </div>
                        {/* Progress bar */}
                        <div
                            style={{
                                width: '200px',
                                height: '4px',
                                backgroundColor: '#e0e0e0',
                                borderRadius: '2px',
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                style={{
                                    width: `${loadingImageProgress.total > 0 ? (loadingImageProgress.current / loadingImageProgress.total) * 100 : 0}%`,
                                    height: '100%',
                                    backgroundColor: '#3498db',
                                    transition: 'width 0.3s ease',
                                }}
                            />
                        </div>
                    </div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            )}

            {/* Page Number Extraction Loading Overlay */}
            {isExtractingPageNumbers && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        backdropFilter: 'blur(2px)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '16px',
                        }}
                    >
                        {/* Spinner */}
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                border: '4px solid #f3f3f3',
                                borderTop: '4px solid #3498db',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                            }}
                        />
                        {/* Loading text */}
                        <div
                            style={{
                                fontSize: '14px',
                                color: '#666',
                                fontWeight: '500',
                            }}
                        >
                            Extracting page numbers from headings...
                        </div>
                    </div>
                </div>
            )}

            {/* Info Dialog */}
            <InfoDialog
                isOpen={infoDialog.isOpen}
                onClose={() => setInfoDialog({ isOpen: false, title: '', message: '' })}
                title={infoDialog.title}
                message={infoDialog.message}
            />
        </div>
    );
};

export default TiptapEditor; 